import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import AuthLayout from '@/layouts/AuthLayout';
import LoginPage from '@/modules/auth/pages/LoginPage';
import ForgotPasswordPage from '@/modules/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/modules/auth/pages/ResetPasswordPage';
import RegisterPage from '@/modules/auth/pages/RegisterPage';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import { DashboardPage, PlaceholderPage } from '@/pages';
import { ChartOfAccountsPage, JournalVouchersPage, FinancialReportsPage, LedgerPage, ProjectLedgerPage, ProjectProfitLossPage, MonthlyProfitLossPage, OperationalProfitPage, CustomerOutstandingPage, SupplierOutstandingPage, CustomerStatementPage, SupplierStatementPage, BankAccountsPage, CurrenciesPage, ReceiptVouchersPage, ReceiptVoucherFormPage, ReceiptVoucherDetailPage, PaymentVouchersPage, PaymentVoucherDetailPage, PaymentVoucherFormPage, PaymentRegisterPage, ReceiptRegisterPage, DepartmentsPage, DepartmentFormPage, PaymentTermsPage, SupplierDebitNotesPage, SupplierDebitNoteDetailPage, CreateDebitNotePage, CustomerCreditNotesPage, CustomerCreditNoteDetailPage, CustomerCreditNoteFormPage, SettlementRegisterPage, VatReturnPage, DirectPurchaseInvoicesPage, DirectPurchaseInvoiceFormPage, DirectPurchaseInvoiceDetailPage, DirectPurchaseInvoiceRegisterPage, SupplierAdvancesReportPage, SupplierAdvanceRefundsPage, SupplierAdvanceRefundFormPage, SupplierAdvanceRefundDetailPage, IntercompanyAffiliatesPage, IntercompanyTransactionsPage, IntercompanyTransactionFormPage, IntercompanyTransactionDetailPage, IntercompanyBalanceReportPage, IntercompanySettlementsPage, IntercompanyStatementPage, IntercompanyGroupsPage, IntercompanyConsolidationPage, PrepaidWorkspacePage, PrepaidContractsPage, PrepaidContractFormPage, PrepaidContractDetailPage, PrepaidAdjustmentPage, PrepaidRunsPage, PrepaidRunNewPage, PrepaidRunDetailPage, PrepaidReconciliationPage, PrepaidSettingsPage, PrepaidDrillRedirectPage, PrepaidRegisterReportPage, PrepaidScheduleReportPage, PrepaidMovementReportPage, PrepaidForecastReportPage } from '@/pages/finance';
import {
  LeadsPage,
  OpportunitiesPage,
  LeadFormPage,
  OpportunityFormPage,
  LeadDetailPage,
  OpportunityDetailPage,
  QuotationsPage,
  QuotationDetailPage,
  QuotationFormPage,
  QuotationDefaultsPage,
  ContractReviewRegisterPage,
  ContractReviewDetailPage,
  OrderReviewChecklistRegisterPage,
  OrderReviewChecklistDetailPage,
  DailyActivityLogPage,
  SalesCalendarPage,
  CrmActivityFormPage,
  FollowUpTrackerPage,
  CrmActivityTypesPage,
  DemoRegisterPage,
  DemoFormPage,
  SalesTargetsPage,
  WeeklySalesPerformancePage,
  StandupRepPage,
  StandupTeamPage,
  StandupHistoryPage,
} from '@/pages/crm';
import {
  QuotationEstimateReconciliationWorkspace,
  QuotationSourceSyncWorkspace,
} from '@/components/crm/quotation-workspace';
import {
  CustomersPage,
  CustomerFormPage,
  SalesOrdersPage,
  SalesOrderDetailPage,
  SalesOrderTimesheetPage,
  SalesOrderTimesheetRegisterPage,
  SalesOrderFormPage,
  SalesOrderStatusCheckPage,
  OrderHealthHubPage,
  QuotationConsolidationPage,
  QuotationConversionReportsPage,
  TradingRentalOverheadReportsPage,
  SalesProjectCostingReportPage,
  SalesConversionFunnelPage,
  SalesWinLossPage,
  SalesOpportunityConversionPage,
  SalesRepPerformancePage,
  SalesProductMixPage,
  SalesBookingBillingBridgePage,
  InlandProcessDeviationsPage,
  SalesOrderToWorkOrderPage,
  DeliveriesPage,
  DeliveryReadinessDashboardPage,
  DeliveryDetailPage,
  DocumentCollectionListPage,
  DocumentCollectionDetailPage,
  DeliveryPrintPreviewPage,
  MyDeliveriesPage,
  SalesInvoicesPage,
  SalesInvoiceDetailPage,
  SupplierInvoicePortalPage,
  RevenueServiceTypeMasterPage,
} from '@/pages/sales';
import SalesTeamCommandCenterPage from '@/pages/sales/SalesTeamCommandCenterPage';
import SupplierAdvanceRequestsPage from '@/pages/procurement/SupplierAdvanceRequestsPage';
import SupplierAdvanceRequestDetailPage from '@/pages/procurement/SupplierAdvanceRequestDetailPage';
import { SuppliersPage, SupplierFormPage, SupplierCategoryMasterPage, PurchaseOrdersPage, PurchaseOrderCreatePage, PurchaseOrderDetailPage, PurchaseOrderEditPage, PurchaseRequestsPage, PurchaseRequestDetailPage, PurchaseRequestFormPage, SupplierInquiriesPage, SupplierInquiryDetailPage, CreateSupplierInquiryPage, SupplierQuotationsPage, SupplierQuotationDetailPage, BidAnalysesPage, BidAnalysisDetailPage, GoodsReceiptNotesPage, CreateGRNPage, GoodsReceiptNoteDetailPage, EditGRNPage, CriticalMtcGapsPage, SupplierInvoicesPage, SupplierInvoiceDetailPage, CreateSupplierInvoicePage, EditSupplierInvoicePage, ThreeWayMatchingPage, PurchaseReturnsPage, PurchaseReturnDetailPage, CreatePurchaseReturnPage, PurchaseReturnEditPage, ServiceReceiptsPage, CreateServiceReceiptPage, ServiceReceiptDetailPage, PoMilestoneRegisterPage } from '@/pages/procurement';
import SpotPurchaseSupervisorPage from '@/pages/procurement/SpotPurchaseSupervisorPage';
import SupplierQuotationPortalPage from '@/pages/procurement/SupplierQuotationPortalPage';
import SupplierSpotPurchasePortalPage from '@/pages/procurement/SupplierSpotPurchasePortalPage';
import SupplierAssessmentPage from '@/pages/procurement/SupplierAssessmentPage';
import CreateSupplierAssessmentPage from '@/pages/procurement/CreateSupplierAssessmentPage';
import SupplierAssessmentDetailPage from '@/pages/procurement/SupplierAssessmentDetailPage';
import SupplierPerformanceDashboard from '@/pages/procurement/SupplierPerformanceDashboard';
import SupplierCriticalityRiskPage from '@/pages/procurement/SupplierCriticalityRiskPage';
import ApprovedSupplierListPage from '@/pages/procurement/ApprovedSupplierListPage';
import Supplier360Page from '@/pages/procurement/Supplier360Page';
import ProcurementControlTowerPage from '@/pages/procurement/ProcurementControlTowerPage';
import SupplierPoPortalPage from '@/pages/procurement/SupplierPoPortalPage';
import Customer360Page from '@/pages/sales/Customer360Page';
import CustomerSuccessPage from '@/pages/sales/CustomerSuccessPage';
import CustomerPortalPage from '@/pages/sales/CustomerPortalPage';
import CustomerSatisfactionRegisterPage from '@/pages/sales/CustomerSatisfactionRegisterPage';
import CustomerSatisfactionPrintPage from '@/pages/sales/CustomerSatisfactionPrintPage';
import CustomerSatisfactionDetailPage from '@/pages/sales/CustomerSatisfactionDetailPage';
import CustomerSatisfactionPublicPage from '@/pages/sales/CustomerSatisfactionPublicPage';
import CustomerPropertyOperationsPage from '@/pages/cspm/CustomerPropertyOperationsPage';
import CustomerPropertyRequirementPage from '@/pages/cspm/CustomerPropertyRequirementPage';
import CustomerPropertyReceiptPage from '@/pages/cspm/CustomerPropertyReceiptPage';
import CustomerPropertyPassportPage from '@/pages/cspm/CustomerPropertyPassportPage';
import CustomerPropertyReturnPage from '@/pages/cspm/CustomerPropertyReturnPage';
import CustomerPropertyReturnAckPublicPage from '@/pages/cspm/CustomerPropertyReturnAckPublicPage';
import CustomerPropertyPortalPublicPage from '@/pages/cspm/CustomerPropertyPortalPublicPage';
import CustomerPropertyReconciliationPage from '@/pages/cspm/CustomerPropertyReconciliationPage';
import ReceivingVerificationPage from '@/pages/procurement/ReceivingVerificationPage';
import OutsourcingManagementPage from '@/pages/procurement/OutsourcingManagementPage';
import SpecificationBuilderPage from '@/pages/procurement/SpecificationBuilderPage';
import {
  ItemsPage,
  ItemFormPage,
  ItemDetailPage,
  StockManagementPage,
  HeatStockPage,
  MaterialRequestsPage,
  MaterialRequestCreatePage,
  MaterialRequestEditPage,
  MaterialRequestDetailPage,
  MaterialIssuesPage,
  MaterialIssueDetailPage,
  MaterialReturnsPage,
  WarehousesPage,
  WarehouseDetailPage,
  ItemClassificationGlPage,
  ItemCategoryMasterPage,
  UomMasterPage,
  EngineeringMaterialsPage,
  StockAdjustmentsPage,
  StockAdjustmentDetailPage,
  StockTransfersPage,
  StockTransferDetailPage,
  OpeningStockUploadPage,
  StockCountsPage,
  StockCountDetailPage,
  PutawayPage,
  ConsignmentReceiptsPage,
  ConsignmentReceiptDetailPage,
  ConsignmentReturnsPage,
  ConsignmentReturnDetailPage,
  DemandPlanningPage,
  LandedCostRevaluePage,
} from '@/pages/inventory';
import ProjectSiteMaterialsPage from '@/pages/inventory/ProjectSiteMaterialsPage';
import { AssetsPage } from '@/pages/asset/AssetsPage';
import AssetFormPage from '@/pages/asset/AssetFormPage';
import AssetViewPage from '@/pages/asset/AssetViewPage';
import AssetScanPage from '@/pages/asset/AssetScanPage';
import AssetMaintenancePage from '@/pages/asset/AssetMaintenancePage';
import WorkshopOperationsPage from '@/pages/assets/workshop/WorkshopOperationsPage';
import WorkshopJobWorkspacePage from '@/pages/assets/workshop/WorkshopJobWorkspacePage';
import {
  AssetMaintenanceAmcFormPage,
  AssetMaintenanceCaseFormPage,
  AssetMaintenancePmCompletePage,
  AssetMaintenancePmFormPage,
} from '@/pages/asset/AssetMaintenanceFormPages';
import AssetDepreciationPage from '@/pages/asset/AssetDepreciationPage';
import AssetDisposalPage from '@/pages/asset/AssetDisposalPage';
import AssetAccountingDefaultsPage from '@/pages/asset/AssetAccountingDefaultsPage';
import AssetGroupsPage from '@/pages/asset/AssetGroupsPage';
import AssetInsurancePage from '@/pages/asset/AssetInsurancePage';
import {
  AssetInsuranceClaimFormPage,
  AssetInsurancePolicyFormPage,
} from '@/pages/asset/AssetInsuranceFormPages';
import AssetAuditPage from '@/pages/asset/AssetAuditPage';
import {
  AssetAuditCycleFormPage,
  AssetAuditCycleWorkspacePage,
  AssetAuditResultFormPage,
} from '@/pages/asset/AssetAuditFormPages';
import AssetAuditReportPage from '@/pages/asset/AssetAuditReportPage';
import {
  AssetMovementFormPage,
  AssetPmvActivateFormPage,
} from '@/pages/asset/AssetMovementFormPages';
import AssetReportsPage from '@/pages/asset/AssetReportsPage';
import { ReportsCatalogPage } from '@/reports/components/ReportsCatalogPage';
import { ReportRunnerPage } from '@/reports/components/ReportRunnerPage';
import ReportsAuditPage from '@/pages/reports/ReportsAuditPage';
import {
  ScmReportsHubPage,
  SalesReportsHubPage,
  MfgReportsHubPage,
  QmsReportsHubPage,
  PrjReportsHubPage,
  EpcReportsHubPage,
  HrReportsHubPage,
  EduReportsHubPage,
} from '@/pages/reports/domainHubs';
import AssetMovementReportPage from '@/pages/asset/AssetMovementReportPage';
import AssetLocationReportPage from '@/pages/asset/AssetLocationReportPage';
import PmvHubPage from '@/pages/assets/pmv/PmvHubPage';
import PmvControlTowerPage from '@/pages/assets/pmv/PmvControlTowerPage';
import PmvRegisterPage from '@/pages/assets/pmv/PmvRegisterPage';
import Pmv360Page from '@/pages/assets/pmv/Pmv360Page';
import PmvSetupPage from '@/pages/assets/pmv/PmvSetupPage';
import {
  PmvClassificationFormPage,
  PmvDocumentRequirementFormPage,
  PmvInspectionTemplateFormPage,
  PmvInternalHireRateFormPage,
} from '@/pages/assets/pmv/PmvSetupFormPages';
import {
  PmvAssignOperatorFormPage,
  PmvChangeStatusFormPage,
  PmvEditSpecificationsFormPage,
  PmvRecordFuelFormPage,
  PmvRecordInspectionFormPage,
  PmvReportBreakdownFormPage,
} from '@/pages/assets/pmv/PmvProfileActionFormPages';
import PmvSiteReceiptFormPage from '@/pages/assets/pmv/PmvSiteReceiptFormPage';
import PmvOperationsBoardPage from '@/pages/assets/pmv/PmvOperationsBoardPage';
import PmvRequestRegisterPage from '@/pages/assets/pmv/PmvRequestRegisterPage';
import PmvRequestWorkspacePage from '@/pages/assets/pmv/PmvRequestWorkspacePage';
import PmvRequestFormPage from '@/pages/assets/pmv/PmvRequestFormPage';
import PmvAllocationWorkspacePage from '@/pages/assets/pmv/PmvAllocationWorkspacePage';
import PmvDailyLogWorkspacePage from '@/pages/assets/pmv/PmvDailyLogWorkspacePage';
import PmvDailyLogRegisterPage from '@/pages/assets/pmv/PmvDailyLogRegisterPage';
import PmvDailyLogSheetListPage from '@/pages/assets/pmv/PmvDailyLogSheetListPage';
import PmvDailyLogSheetPage from '@/pages/assets/pmv/PmvDailyLogSheetPage';
import PmvFieldOpsExceptionsPage from '@/pages/assets/pmv/PmvFieldOpsExceptionsPage';
import PmvHireControlTowerPage from '@/pages/assets/pmv/PmvHireControlTowerPage';
import PmvHireRegisterPage from '@/pages/assets/pmv/PmvHireRegisterPage';
import PmvHireAgreementPage from '@/pages/assets/pmv/PmvHireAgreementPage';
import PmvHireOnOffHirePage from '@/pages/assets/pmv/PmvHireOnOffHirePage';
import PmvHireTimesheetPage from '@/pages/assets/pmv/PmvHireTimesheetPage';
import PmvHireStatementPage from '@/pages/assets/pmv/PmvHireStatementPage';
import PmvHireInvoiceReconPage from '@/pages/assets/pmv/PmvHireInvoiceReconPage';
import PmvHireAttentionPage from '@/pages/assets/pmv/PmvHireAttentionPage';
import {
  PmvConnectedOverviewPage,
  PmvConnectedDevicesPage,
  PmvConnectedProvidersPage,
  PmvConnectedDiagnosticsPage,
  PmvConnectedReconciliationPage,
} from '@/pages/assets/pmv/PmvConnectedPages';
import PmvFleetMapPage from '@/pages/assets/pmv/PmvFleetMapPage';
import { PmvGeofenceListPage, PmvGeofenceFormPage } from '@/pages/assets/pmv/PmvGeofencePages';
import PmvSpatialAttentionPage from '@/pages/assets/pmv/PmvSpatialAttentionPage';
import PmvTyreSchematicPage from '@/pages/assets/pmv/PmvTyreSchematicPage';
import PmvBatterySchematicPage from '@/pages/assets/pmv/PmvBatterySchematicPage';
import PmvComponentRegisterPage from '@/pages/assets/pmv/PmvComponentRegisterPage';
import PmvComponentPassportPage from '@/pages/assets/pmv/PmvComponentPassportPage';
import PmvComponentControlTowerPage from '@/pages/assets/pmv/PmvComponentControlTowerPage';
import PmvComponentImportPage from '@/pages/assets/pmv/PmvComponentImportPage';
import PmvFuelControlTowerPage from '@/pages/assets/pmv/PmvFuelControlTowerPage';
import PmvReliabilityControlTowerPage from '@/pages/assets/pmv/PmvReliabilityControlTowerPage';
import PmvReliabilityRegisterPage from '@/pages/assets/pmv/PmvReliabilityRegisterPage';
import PmvReliabilityAttentionPage from '@/pages/assets/pmv/PmvReliabilityAttentionPage';
import PmvReliabilityFailuresPage from '@/pages/assets/pmv/PmvReliabilityFailuresPage';
import PmvReliabilityFaultsPage from '@/pages/assets/pmv/PmvReliabilityFaultsPage';
import PmvReliabilityPredictionsPage from '@/pages/assets/pmv/PmvReliabilityPredictionsPage';
import PmvReliabilityForecastPage from '@/pages/assets/pmv/PmvReliabilityForecastPage';
import PmvReliabilityConditionPage from '@/pages/assets/pmv/PmvReliabilityConditionPage';
import PmvFuelRegisterPage from '@/pages/assets/pmv/PmvFuelRegisterPage';
import PmvFuelReconciliationPage from '@/pages/assets/pmv/PmvFuelReconciliationPage';
import PmvFuelAttentionPage from '@/pages/assets/pmv/PmvFuelAttentionPage';
import PmvFuelSourcesPage from '@/pages/assets/pmv/PmvFuelSourcesPage';
import ProjectPmvPage from '@/pages/projects/ProjectPmvPage';
import {
  ProjectsPage,
  ProjectFormPage,
  ProjectTimesheetsPage,
  ProjectCostsPage,
  ProjectPortfolioDashboardPage,
  ProjectHealthHubPage,
  ProjectStatusCheckPage,
  ProjectBudgetWorkbenchPage,
  ProjectBudgetRevisionBridgePage,
  ProjectBudgetExceedanceBridgePage,
  ProjectBudgetHubPage,
  ProjectBudgetVsActualPage,
} from '@/pages/projects';
import {
  QualityPlansPage,
  QualityPlanDetailPage,
  InspectionsPage,
  InspectionDetailPage,
  ReceivingInspectionsPage,
  ReceivingInspectionDetailPage,
  ReceivingDimensionalInspectionsPage,
  ReceivingDimensionalInspectionDetailPage,
  ReceivingInspectionPrintPage,
  ReceivingDimensionalInspectionPrintPage,
  NCRsPage,
  NCRDetailPage,
  NCRFormPage,
  NCRPrintPage,
  CAPAsPage,
  CAPADetailPage,
  CAPAFormPage,
  CAPAPrintPage,
  QualityDashboardPage,
  AuditSchedulesPage,
  InspectionPointLibraryPage,
  InitialInspectionRequestsPage,
  FinalInspectionReportsPage,
  FinalInspectionReportDetailPage,
  FinalInspectionReportPrintPage,
  InspectionReleaseNotesPage,
  InspectionReleaseNoteDetailPage,
  InspectionReleaseNotePrintPage,
  CertificateOfConformityPage,
  CertificateOfConformityPrintPage,
  DimensionalInspectionPage,
  DimensionalInspectionRegisterPage,
  DimensionalInspectionPrintPage,
} from '@/pages/quality';
import DepartmentKPIsPage from '@/pages/quality/DepartmentKPIsPage';
import KPIMeasurementsPage from '@/pages/quality/KPIMeasurementsPage';
import { RiskAssessmentsPage, RiskAssessmentWorkspacePage } from '@/pages/risk';
import { MOCRegisterPage, MOCNewPage, MOCDetailPage, MOCReportsPage } from '@/pages/grc';
import {
  AssessmentCyclesPage,
  AssessmentDetailPage as CompetencyAssessmentDetailPage,
  AssessmentsPage as CompetencyAssessmentsPage,
  CompetencyEmployeePage,
  CompetencyImportPage,
  CompetencyPage,
  EffectivenessDetailPage,
  EffectivenessListPage,
  GapMatrixPage,
  JobProfileDetailPage,
  JobProfilesPage,
  TrainingCoursesPage,
  TrainingSchedulePage,
  TrainingSessionDetailPage,
  TrainingSessionsPage,
} from '@/pages/competency';
import {
  CalibrationPage,
  CalibrationScheduleFormPage,
  CalibrationRecordFormPage,
  CalibrationRecordDetailPage,
} from '@/pages/calibration';
import {
  TmmdeEquipmentListPage,
  TmmdeEquipmentDetailPage,
  TmmdeImpactListPage,
  TmmdeImpactDetailPage,
} from '@/pages/tmmde';
import { BOMsPage, BOMFormPage, WorkOrdersPage, WorkOrderOperationsBoardPage, EodClosePage, ShiftsPage, ProductionCalendarPage, WorkstationsPage, CapacityPlanningPage, MaterialAuditPage, MasterSchedulePage, JobSchedulingPage, OperationsMasterPage, OperationMasterFormPage, BOMRoutingPage, OperationDependenciesPage, OperationTimeTrackingPage, InlandProcessDeviationDetailPage, TraceabilityMatrixPage } from '@/pages/manufacturing';
import StageMasterPage from '@/pages/manufacturing/StageMasterPage';
import ManufacturingDashboardPage from '@/pages/manufacturing/ManufacturingDashboardPage';
import ManufacturingControlTowerPage from '@/pages/manufacturing/ManufacturingControlTowerPage';
import FactoryFloorDisplayPage from '@/pages/manufacturing/FactoryFloorDisplayPage';
import WorkstationControlCenterPage from '@/pages/manufacturing/WorkstationControlCenterPage';
import ProductionRequestsPage from '@/pages/manufacturing/ProductionRequestsPage';
import ProductionRequestFormPage from '@/pages/manufacturing/ProductionRequestFormPage';
import ProductionRequestToWorkOrderPage from '@/pages/manufacturing/ProductionRequestToWorkOrderPage';
import ProductionRequestApprovalPrintPage from '@/pages/manufacturing/ProductionRequestApprovalPrintPage';
import WorkOrderPrintPage from '@/pages/manufacturing/WorkOrderPrintPage';
import WorkOrderRouteSheetPage from '@/pages/manufacturing/WorkOrderRouteSheetPage';
import RouteSheetRegisterPage from '@/pages/manufacturing/RouteSheetRegisterPage';
import OutsourcedFabricationReportsPage from '@/pages/manufacturing/OutsourcedFabricationReportsPage';
import { EstimationDashboardPage, EstimationBuilderPage, MultiItemEstimationBuilderPage, AICostPredictorPage, VersionComparisonPage, MarginRulesPage, HistoricalAnalysisPage, BOQListPage, BOQBuilderPage, BOQImportWizard, TradingRentalEstimationsPage, TradingRentalEstimationBuilderPage, AssemblyEstimateListPage, AssemblyEstimateBuilderPage } from '@/pages/estimation';
import {
  EmployeesPage,
  EmployeeFormPage,
  EmployeeLocationAssignmentPage,
  EmployeeShiftAssignmentPage,
  EmployeeProfilePage,
  ProfileChangeRequestsPage,
  LeavesPage,
  LeaveApplicationFormPage,
  LeaveApplicationDetailPage,
  LeaveBulkAbsencePage,
  LeaveTypesPage,
  LeaveOpeningBalancesPage,
  LeaveSettlementsPage,
  LeaveSettlementDetailPage,
  HrmsSetupPage,
  AccrualManagementPage,
  HolidaysPage,
  ShiftsPage as HrShiftsPage,
  SalaryHeadsPage,
  ContractsPage,
  ContractFormPage,
  ContractDetailPage,
  DocumentExpiryPage,
  LocationsPage,
  PositionsPage,
  GradesPage,
  AuditLogPage,
  EssHomePage,
  EssLeavePage,
  EssOtPage,
  EssPayslipPage,
  EssProfilePage,
  EssLettersPage,
  EssTimesheetsPage,
  LetterRequestsPage,
  AttendancePage,
  AttendanceMonthDetailPage,
  TimesheetAutoGeneratePage,
  OvertimePage,
  OvertimeFormPage,
  OvertimeDetailPage,
  AdjustmentsPage,
  AdjustmentsFormPage,
  LoansPage,
  LoanFormPage,
  LoanDetailPage,
  PayrollPage,
  PayrollPeriodFormPage,
  PayrollPeriodDetailPage,
  EosbPage,
  EosbFormPage,
  EosbDetailPage,
  EosbEmployeeProfilePage,
  AllocationsPage,
  AllocationsBulkPage,
  AssetCustodyPage,
  AssetIssueFormPage,
  AssetIssueDetailPage,
  SalaryRevisionsPage,
  SalaryRevisionFormPage,
  SalaryRevisionDetailPage,
  RequisitionsPage,
  RequisitionDetailPage,
  JobsPage,
  JobDetailPage,
  CandidatesPage,
  CandidateDetailPage,
  ApplicationsPage,
  ApplicationDetailPage,
  InterviewsPage,
  OffersPage,
  OfferDetailPage,
  OnboardingPage,
  OnboardingDetailPage,
  CareersSettingsPage,
  RecruitReportsPage,
  AssessmentsPage,
  AssessmentDetailPage,
  AssessmentFormPage,
  RequisitionFormPage,
  JobFormPage,
  CandidateFormPage,
  ApplicationFormPage,
  InterviewFormPage,
  OfferFormPage,
  OnboardingFormPage,
  RecruitHomePage,
  CandidateImportPage,
  InterviewDetailPage,
  InterviewScorecardPage,
  MyInterviewsPage,
  OfferConvertPage,
  MyOnboardingTasksPage,
  HrmsDashboardPage,
} from '@/pages/hr';
import {
  CareersListPage,
  CareersJobPage,
  OfferAcceptPage,
  OnboardingPortalPage,
} from '@/pages/public/careers';
import CompanyDocumentLibraryPage from '@/pages/documents/CompanyDocumentLibraryPage';
import { UAEEInvoicingPage, KSAZATCAPage, InboundEInvoicesPage } from '@/pages/compliance';
import HomePage from '@/pages/home/HomePage';
import EducationHomePage from '@/pages/education/EducationHomePage';
import EducationSettingsPage from '@/pages/education/EducationSettingsPage';
import StudentListPage from '@/pages/education/StudentListPage';
import StudentFormPage from '@/pages/education/StudentFormPage';
import StudentDetailPage from '@/pages/education/StudentDetailPage';
import CompartmentRecordPage from '@/pages/education/CompartmentRecordPage';
import AcademicSessionsPage from '@/pages/education/AcademicSessionsPage';
import AcademicSessionFormPage from '@/pages/education/AcademicSessionFormPage';
import AcademicSessionDetailPage from '@/pages/education/AcademicSessionDetailPage';
import PolicyPacksPage from '@/pages/education/PolicyPacksPage';
import AdmissionsListPage from '@/pages/education/AdmissionsListPage';
import AdmissionsCrmPage from '@/pages/education/AdmissionsCrmPage';
import EnquiryListPage from '@/pages/education/EnquiryListPage';
import EnquiryFormPage from '@/pages/education/EnquiryFormPage';
import SchoolCalendarPage from '@/pages/education/SchoolCalendarPage';
import AdmissionApplicationPage from '@/pages/education/AdmissionApplicationPage';
import AdmissionReviewPage from '@/pages/education/AdmissionReviewPage';
import AdmissionDecisionPage from '@/pages/education/AdmissionDecisionPage';
import AdmissionOfferPage from '@/pages/education/AdmissionOfferPage';
import EnrollmentRegisterPage from '@/pages/education/EnrollmentRegisterPage';
import EnrollmentDetailPage from '@/pages/education/EnrollmentDetailPage';
import SectionAllocationPage from '@/pages/education/SectionAllocationPage';
import SectionTransferPage from '@/pages/education/SectionTransferPage';
import ProgressionListPage from '@/pages/education/ProgressionListPage';
import ProgressionDetailPage from '@/pages/education/ProgressionDetailPage';
import ProgressionExceptionsPage from '@/pages/education/ProgressionExceptionsPage';
import WithdrawalPage from '@/pages/education/WithdrawalPage';
import TransferCertificateListPage from '@/pages/education/TransferCertificateListPage';
import TransferCertificateDetailPage from '@/pages/education/TransferCertificateDetailPage';
import FeesHubPage from '@/pages/education/FeesHubPage';
import FeeHeadsPage from '@/pages/education/FeeHeadsPage';
import FeeStructuresPage from '@/pages/education/FeeStructuresPage';
import FeeStructureDetailPage from '@/pages/education/FeeStructureDetailPage';
import FeeDemandsPage from '@/pages/education/FeeDemandsPage';
import FeeOutstandingPage from '@/pages/education/FeeOutstandingPage';
import FeeCollectionWorkbenchPage from '@/pages/education/FeeCollectionWorkbenchPage';
import StudentFeeLedgerPage from '@/pages/education/StudentFeeLedgerPage';
import FeeConcessionsPage from '@/pages/education/FeeConcessionsPage';
import FeeFamilyStatementPage from '@/pages/education/FeeFamilyStatementPage';
import FeePromisesPage from '@/pages/education/FeePromisesPage';
import FeeDisputesPage from '@/pages/education/FeeDisputesPage';
import FeeDisputeResolvePage from '@/pages/education/FeeDisputeResolvePage';
import TransportHubPage from '@/pages/education/TransportHubPage';
import TransportRoutesPage from '@/pages/education/TransportRoutesPage';
import TransportBoardingPage from '@/pages/education/TransportBoardingPage';
import TransportBoardingRunPage from '@/pages/education/TransportBoardingRunPage';
import TransportRouteDetailPage from '@/pages/education/TransportRouteDetailPage';
import TransportEnrollmentsPage from '@/pages/education/TransportEnrollmentsPage';
import TransportEnrollmentFormPage from '@/pages/education/TransportEnrollmentFormPage';
import TransportEnrollmentBulkPage from '@/pages/education/TransportEnrollmentBulkPage';
import LibraryHubPage from '@/pages/education/LibraryHubPage';
import LibraryTitlesPage from '@/pages/education/LibraryTitlesPage';
import LibraryTitleFormPage from '@/pages/education/LibraryTitleFormPage';
import LibraryTitleCopiesPage from '@/pages/education/LibraryTitleCopiesPage';
import LibraryIssuePage from '@/pages/education/LibraryIssuePage';
import LibraryLoansPage from '@/pages/education/LibraryLoansPage';
import LibraryReservationsPage from '@/pages/education/LibraryReservationsPage';
import LibraryReservationFormPage from '@/pages/education/LibraryReservationFormPage';
import LibrarySettingsPage from '@/pages/education/LibrarySettingsPage';
import AssessmentHubPage from '@/pages/education/AssessmentHubPage';
import AssessmentSchemePage from '@/pages/education/AssessmentSchemePage';
import AssessmentEntryPage from '@/pages/education/AssessmentEntryPage';
import AssessmentPublishPage from '@/pages/education/AssessmentPublishPage';
import QuestionBankHubPage from '@/pages/education/QuestionBankHubPage';
import QuestionItemsPage from '@/pages/education/QuestionItemsPage';
import QuestionItemFormPage from '@/pages/education/QuestionItemFormPage';
import QuestionBlueprintsPage from '@/pages/education/QuestionBlueprintsPage';
import QuestionBlueprintDetailPage from '@/pages/education/QuestionBlueprintDetailPage';
import HpcHubPage from '@/pages/education/HpcHubPage';
import HpcTemplateFormPage from '@/pages/education/HpcTemplateFormPage';
import HpcStageFormPage from '@/pages/education/HpcStageFormPage';
import HpcStageDetailPage from '@/pages/education/HpcStageDetailPage';
import LmsHubPage from '@/pages/education/LmsHubPage';
import LessonPlanListPage from '@/pages/education/LessonPlanListPage';
import LessonPlanFormPage from '@/pages/education/LessonPlanFormPage';
import HomeworkListPage from '@/pages/education/HomeworkListPage';
import HomeworkFormPage from '@/pages/education/HomeworkFormPage';
import HomeworkDetailPage from '@/pages/education/HomeworkDetailPage';
import HomeworkSubmitPage from '@/pages/education/HomeworkSubmitPage';
import ResourceListPage from '@/pages/education/ResourceListPage';
import ResourceFormPage from '@/pages/education/ResourceFormPage';
import ClassAnnouncementListPage from '@/pages/education/ClassAnnouncementListPage';
import ClassAnnouncementFormPage from '@/pages/education/ClassAnnouncementFormPage';
import ActivitiesHubPage from '@/pages/education/ActivitiesHubPage';
import ActivitiesListPage from '@/pages/education/ActivitiesListPage';
import ActivityFormPage from '@/pages/education/ActivityFormPage';
import ActivityMembersPage from '@/pages/education/ActivityMembersPage';
import ActivityMeetingsPage from '@/pages/education/ActivityMeetingsPage';
import ActivityMeetingAttendancePage from '@/pages/education/ActivityMeetingAttendancePage';
import ActivityAchievementsPage from '@/pages/education/ActivityAchievementsPage';
import ActivityAchievementFormPage from '@/pages/education/ActivityAchievementFormPage';
import ActivityMembershipsPage from '@/pages/education/ActivityMembershipsPage';
import ActivityMembershipFormPage from '@/pages/education/ActivityMembershipFormPage';
import ActivityMembershipBulkPage from '@/pages/education/ActivityMembershipBulkPage';
import BehaviourListPage from '@/pages/education/BehaviourListPage';
import BehaviourFormPage from '@/pages/education/BehaviourFormPage';
import BehaviourDetailPage from '@/pages/education/BehaviourDetailPage';
import InclusionHubPage from '@/pages/education/InclusionHubPage';
import InclusionAccommodationListPage from '@/pages/education/InclusionAccommodationListPage';
import InclusionAccommodationFormPage from '@/pages/education/InclusionAccommodationFormPage';
import InclusionAccommodationDetailPage from '@/pages/education/InclusionAccommodationDetailPage';
import InclusionScreeningListPage from '@/pages/education/InclusionScreeningListPage';
import InclusionScreeningFormPage from '@/pages/education/InclusionScreeningFormPage';
import InclusionScreeningDetailPage from '@/pages/education/InclusionScreeningDetailPage';
import InterventionListPage from '@/pages/education/InterventionListPage';
import InterventionFormPage from '@/pages/education/InterventionFormPage';
import InterventionDetailPage from '@/pages/education/InterventionDetailPage';
import StandardsListPage from '@/pages/education/StandardsListPage';
import StandardsFormPage from '@/pages/education/StandardsFormPage';
import StandardsDetailPage from '@/pages/education/StandardsDetailPage';
import GovernanceHubPage from '@/pages/education/GovernanceHubPage';
import GovernancePolicyListPage from '@/pages/education/GovernancePolicyListPage';
import GovernancePolicyFormPage from '@/pages/education/GovernancePolicyFormPage';
import GovernancePolicyDetailPage from '@/pages/education/GovernancePolicyDetailPage';
import GovernanceCommitteeListPage from '@/pages/education/GovernanceCommitteeListPage';
import GovernanceCommitteeFormPage from '@/pages/education/GovernanceCommitteeFormPage';
import GovernanceCommitteeDetailPage from '@/pages/education/GovernanceCommitteeDetailPage';
import GovernanceMeetingDetailPage from '@/pages/education/GovernanceMeetingDetailPage';
import AppPlanListPage from '@/pages/education/AppPlanListPage';
import AppPlanFormPage from '@/pages/education/AppPlanFormPage';
import AppPlanDetailPage from '@/pages/education/AppPlanDetailPage';
import EqamsListPage from '@/pages/education/EqamsListPage';
import EqamsFormPage from '@/pages/education/EqamsFormPage';
import EqamsDetailPage from '@/pages/education/EqamsDetailPage';
import ParentPortalPage from '@/pages/education/ParentPortalPage';
import ParentPortalChildPage from '@/pages/education/ParentPortalChildPage';
import StudentPortalPage from '@/pages/education/StudentPortalPage';
import TeacherPortalPage from '@/pages/education/TeacherPortalPage';
import UdiseHubPage from '@/pages/education/UdiseHubPage';
import UdiseMapListPage from '@/pages/education/UdiseMapListPage';
import UdiseRunListPage from '@/pages/education/UdiseRunListPage';
import UdiseRunFormPage from '@/pages/education/UdiseRunFormPage';
import UdiseRunDetailPage from '@/pages/education/UdiseRunDetailPage';
import IdsHubPage from '@/pages/education/IdsHubPage';
import IdsWorkflowListPage from '@/pages/education/IdsWorkflowListPage';
import IdsWorkflowFormPage from '@/pages/education/IdsWorkflowFormPage';
import IdsWorkflowDetailPage from '@/pages/education/IdsWorkflowDetailPage';
import IdsReadinessPage from '@/pages/education/IdsReadinessPage';
import CommsHubPage from '@/pages/education/CommsHubPage';
import PrintHubPage from '@/pages/education/PrintHubPage';
import PrincipalPulsePage from '@/pages/education/PrincipalPulsePage';
import QualityCopilotPage from '@/pages/education/QualityCopilotPage';
import StudentDigitalTwinPage from '@/pages/education/StudentDigitalTwinPage';
import ProcessInsightsPage from '@/pages/education/ProcessInsightsPage';
import CommsTemplateListPage from '@/pages/education/CommsTemplateListPage';
import CommsSendListPage from '@/pages/education/CommsSendListPage';
import CommsSendFormPage from '@/pages/education/CommsSendFormPage';
import CommsSendDetailPage from '@/pages/education/CommsSendDetailPage';
import TimetableHubPage from '@/pages/education/TimetableHubPage';
import SmartTimetableStudioPage from '@/pages/education/smart-timetable/SmartTimetableStudioPage';
import SmartTimetableSetupPage from '@/pages/education/smart-timetable/SmartTimetableSetupPage';
import SmartTimetableScenariosPage from '@/pages/education/smart-timetable/SmartTimetableScenariosPage';
import SmartTimetableScenarioDetailPage from '@/pages/education/smart-timetable/SmartTimetableScenarioDetailPage';
import SmartTimetableRunPage from '@/pages/education/smart-timetable/SmartTimetableRunPage';
import TimetablePeriodsPage from '@/pages/education/TimetablePeriodsPage';
import TimetablePeriodFormPage from '@/pages/education/TimetablePeriodFormPage';
import TimetableRoomsPage from '@/pages/education/TimetableRoomsPage';
import TimetableRoomFormPage from '@/pages/education/TimetableRoomFormPage';
import TimetableGridsPage from '@/pages/education/TimetableGridsPage';
import TimetableGridDetailPage from '@/pages/education/TimetableGridDetailPage';
import TimetableSlotFormPage from '@/pages/education/TimetableSlotFormPage';
import TimetableTeacherPage from '@/pages/education/TimetableTeacherPage';
import SubstitutionsListPage from '@/pages/education/SubstitutionsListPage';
import SubstitutionDayFormPage from '@/pages/education/SubstitutionDayFormPage';
import SubstitutionDayDetailPage from '@/pages/education/SubstitutionDayDetailPage';
import SubstitutionCoverAssignPage from '@/pages/education/SubstitutionCoverAssignPage';
import CurriculumHubPage from '@/pages/education/CurriculumHubPage';
import CurriculumUnitsPage from '@/pages/education/CurriculumUnitsPage';
import CurriculumUnitFormPage from '@/pages/education/CurriculumUnitFormPage';
import CurriculumUnitDetailPage from '@/pages/education/CurriculumUnitDetailPage';
import CurriculumOutcomeFormPage from '@/pages/education/CurriculumOutcomeFormPage';
import CurriculumImportPage from '@/pages/education/CurriculumImportPage';
import TeacherWorkspacePage from '@/pages/education/TeacherWorkspacePage';
import AcademicHistoryPage from '@/pages/education/AcademicHistoryPage';
import AttendanceRegisterPage from '@/pages/education/AttendanceRegisterPage';
import TakeAttendancePage from '@/pages/education/TakeAttendancePage';
import StudentAttendanceHistoryPage from '@/pages/education/StudentAttendanceHistoryPage';
import StudentLeavePage from '@/pages/education/StudentLeavePage';
import AttendanceCorrectionsPage from '@/pages/education/AttendanceCorrectionsPage';
import AttendanceOverviewPage from '@/pages/education/AttendanceOverviewPage';
import AttendanceExceptionsPage from '@/pages/education/AttendanceExceptionsPage';
import AttendanceSettingsPage from '@/pages/education/AttendanceSettingsPage';
import MyTasksPage from '@/pages/home/MyTasksPage';
import UnifiedTaskDetailPage from '@/pages/home/UnifiedTaskDetailPage';
import TeamTasksPage from '@/pages/home/TeamTasksPage';
import ModuleTasksRedirect from '@/pages/home/ModuleTasksRedirect';
import MyDraftsPage from '@/pages/home/MyDraftsPage';
import MyAlertsPage from '@/pages/home/MyAlertsPage';
import ApprovalInboxPage from '@/pages/home/ApprovalInboxPage';
import ExceptionsDashboard from '@/pages/home/ExceptionsDashboard';
import CompanyOverviewPage from '@/pages/home/CompanyOverviewPage';
import ExecutiveDashboard from '@/pages/analytics/ExecutiveDashboard';
import SCMAnalytics from '@/pages/analytics/SCMAnalytics';
import MFGAnalytics from '@/pages/analytics/MFGAnalytics';
import QualityAnalytics from '@/pages/analytics/QualityAnalytics';
import AiWorkspacePage from '@/pages/ai/AiWorkspacePage';
import AiDashboardPage from '@/pages/ai/AiDashboardPage';
import AiUdqBuilderPage from '@/pages/ai/AiUdqBuilderPage';
import EnterpriseMemoryPage from '@/pages/ai/EnterpriseMemoryPage';
import EnterpriseMemoryDetailPage from '@/pages/ai/EnterpriseMemoryDetailPage';
import HelpCenterPage from '@/pages/help/HelpCenterPage';
import HelpGuideDetailPage from '@/pages/help/HelpGuideDetailPage';
import AiWorkspaceShell from '@/layouts/AiWorkspaceShell';
import OwnerIntelligenceShell from '@/layouts/OwnerIntelligenceShell';
import {
  OwnerIntelligenceHomePage,
  GroupGmDashboardPage,
  BusinessHealthPage,
  FinancialHealthPage,
  GrowthProfitPage,
  CashIntelligencePage,
  CustomerIntelligencePage,
  OperationsIntelligencePage,
  SupplyChainIntelligencePage,
  RiskCenterPage,
  ScenarioManagerPage,
  ExecutiveAdvisorPage,
  BoardPackPage,
  KpiPerformancePage,
  ExecutiveActionCenterPage,
  RootCausePage,
  BenchmarkPage,
  StrategyAlignmentPage,
  ExecutiveMemoryPage,
  MeetingModePage,
  ExternalContextPage,
  AiGovernancePage,
  OrganizationalKnowledgePage,
  OperationalIntelligencePage,
  OperationalActionsPage,
  DigitalTwinPage,
  ModuleIntelligencePage,
  AutomationMonitorPage,
  AutomationExecutionPage,
  AutomationDesignerPage,
  EnterpriseStrategyPage,
  EnterpriseReadinessPage,
} from '@/pages/owner-intelligence';
import SalesPipelineIntelligencePage from '@/pages/owner-intelligence/SalesPipelineIntelligencePage';
import { ResponsibilityRulesPage } from '@/pages/platform/ResponsibilityRulesPage';
import { 
  SystemSettingsPage, 
  CompanyProfilePage,
  DocumentSequencesPage,
  ApprovalWorkflowsPage,
  ApprovalWorkflowFormPage,
  EmailTemplatesPage,
  ApiKeysPage,
  UsersPage,
  UserCreatePage,
  UserEditPage,
  RolesPage,
  PermissionsPage,
  UserPreferencesPage,
  UnifiedTaskSlaDefinitionsPage,
  RejectionReasonsSettingsPage,
  SalesTeamsPage,
  SalesTeamTransferRegisterPage,
  MissingTeamAssignmentPage,
} from '@/pages/settings';
import ConfigurationStudioPage from '@/pages/platform/ConfigurationStudioPage';
import SystemStatusPage from '@/pages/platform/SystemStatusPage';
import PlatformCompaniesPage from '@/pages/platform/PlatformCompaniesPage';
import PlatformTenantDetailPage from '@/pages/platform/PlatformTenantDetailPage';
import CreateTenantWizardPage from '@/pages/platform/CreateTenantWizardPage';
import EpcHubPage from '@/pages/epc/EpcHubPage';
import EpcConstructionExecutionPage from '@/pages/epc/execution/EpcConstructionExecutionPage';
import EpcCommercialHubPage from '@/pages/epc/commercial/EpcCommercialHubPage';
import CommercialPursuitWorkspacePage from '@/pages/epc/commercial/CommercialPursuitWorkspacePage';
import PursuitResolveBridgePage from '@/pages/epc/commercial/PursuitResolveBridgePage';
import EpcPrequalificationsPage from '@/pages/epc/commercial/EpcPrequalificationsPage';
import EpcPrequalificationDetailPage from '@/pages/epc/commercial/EpcPrequalificationDetailPage';
import EpcInvitationCreatePage from '@/pages/epc/commercial/EpcInvitationCreatePage';
import EpcEoiContinuePage from '@/pages/epc/commercial/EpcEoiContinuePage';
import EpcTendersPage from '@/pages/epc/commercial/EpcTendersPage';
import EpcTenderWorkspacePage from '@/pages/epc/commercial/EpcTenderWorkspacePage';
import EpcBidControlRoomPage from '@/pages/epc/commercial/EpcBidControlRoomPage';
import EpcBidDecisionsPage from '@/pages/epc/commercial/EpcBidDecisionsPage';
import EpcAwardsPage from '@/pages/epc/commercial/EpcAwardsPage';
import EpcContractsPage from '@/pages/epc/commercial/EpcContractsPage';
import EpcContractCreatePage from '@/pages/epc/commercial/EpcContractCreatePage';
import EpcContractAdminWorkspacePage from '@/pages/epc/commercial/EpcContractAdminWorkspacePage';
import EpcContractAdminControlPage from '@/pages/epc/commercial/EpcContractAdminControlPage';
import EpcPaymentApplicationFormPage from '@/pages/epc/commercial/EpcPaymentApplicationFormPage';
import EpcIpcRegisterPage from '@/pages/epc/ipc/EpcIpcRegisterPage';
import EpcCertificationsRegisterPage from '@/pages/epc/ipc/EpcCertificationsRegisterPage';
import EpcSubcontractsPage from '@/pages/epc/commercial/EpcSubcontractsPage';
import EpcSubcontractAgreementFormPage from '@/pages/epc/commercial/EpcSubcontractAgreementFormPage';
import EpcSubcontractPaymentCertificateFormPage from '@/pages/epc/commercial/EpcSubcontractPaymentCertificateFormPage';
import EpcSubcontractWorkspacePage from '@/pages/epc/commercial/EpcSubcontractWorkspacePage';
import EpcSubcontractControlPage from '@/pages/epc/commercial/EpcSubcontractControlPage';
import EpcCaDocumentBridgePage from '@/pages/epc/commercial/EpcCaDocumentBridgePage';
import EpcEstimateRevisionBridgePage from '@/pages/epc/commercial/EpcEstimateRevisionBridgePage';
import EpcMobilizationsPage from '@/pages/epc/commercial/EpcMobilizationsPage';
import EpcCommercialControlTowerPage from '@/pages/epc/commercial/EpcCommercialControlTowerPage';
import EpcWbsLibraryPage from '@/pages/epc/libraries/EpcWbsLibraryPage';
import EpcEstimatingLibrariesPage from '@/pages/epc/libraries/EpcEstimatingLibrariesPage';
import { UserProfilePage } from '@/pages/profile/UserProfilePage';
import { AiAgentAuditPage } from '@/pages/platform/AiAgentAuditPage';
import { AiAgentConfigPage } from '@/pages/platform/AiAgentConfigPage';
import { RecognitionLabPage } from '@/pages/platform/RecognitionLabPage';
import { RecognitionAnalyticsPage } from '@/pages/platform/RecognitionAnalyticsPage';
import RejectionRegisterPage from '@/pages/platform/RejectionRegisterPage';
import { 
  MastersDashboard,
  CoreOperationsDashboard,
  FinanceDashboard,
  SupplyChainDashboard,
  SalesDashboard,
  UnifiedEnterpriseDashboard,
  QualityComplianceDashboard,
} from '@/pages/modules';
import ProtectedRoute from '@/components/ProtectedRoute';
import { CompanyLetterheadSync } from '@/components/print/CompanyLetterheadSync';
import { CurrencyProvider } from '@/contexts/CurrencyContext';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { UserPreferencesProvider } from '@/contexts/UserPreferencesContext';
import { LocaleProvider } from '@/i18n/LocaleProvider';
import { MenuProvider } from '@/providers/MenuProvider';
import { RouteRedirect, LegacyPrefixRedirect } from '@/components/RouteRedirect';

/** Remount payment voucher form when switching /new vs /:id/edit so local state and fields reset. */
function PaymentVoucherFormRoute() {
  const { pathname } = useLocation();
  return <PaymentVoucherFormPage key={pathname} />;
}

function RedirectReceivingInspectionDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/scm/procurement/receiving-inspections/${id}`} replace />;
}

function RedirectReceivingDimensionalInspectionDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/qms/receiving-dimensional-inspections/${id}`} replace />;
}

function App() {
  const { initializeAuth } = useAuthStore();

  useEffect(() => {
    initializeAuth();
  }, []); // Only run once on mount


  return (
    <UserPreferencesProvider>
      <LocaleProvider>
        <AntdApp>
        <CurrencyProvider>
          <NavigationProvider>
            <MenuProvider>
              <RouteRedirect />
              <CompanyLetterheadSync />
              <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
        </Route>
        <Route element={<AuthLayout brand="withu" />}>
          <Route path="/loginwithu" element={<LoginPage />} />
        </Route>

        {/* Public supplier quotation portal (no auth) */}
        <Route path="/supplier-quotation/:token" element={<SupplierQuotationPortalPage />} />
        <Route path="/supplier-spot-purchase/:token" element={<SupplierSpotPurchasePortalPage />} />
        <Route path="/supplier-po/:token" element={<SupplierPoPortalPage />} />
        <Route path="/customer-portal/:token" element={<CustomerPortalPage />} />
        <Route path="/customer-satisfaction/:token" element={<CustomerSatisfactionPublicPage />} />
        <Route
          path="/cspm/public/returns/:token/acknowledge"
          element={<CustomerPropertyReturnAckPublicPage />}
        />
        <Route path="/cspm/public/portal/:token" element={<CustomerPropertyPortalPublicPage />} />
        {/* Public supplier purchase-bill confirmation (NM_WP_15); URL uses sales/invoices for shared comms */}
        <Route path="/growth/invoices/supplier/:token" element={<SupplierInvoicePortalPage />} />

        {/* Public HRMS careers / offer / onboarding portals */}
        <Route path="/careers/:slug/jobs/:jobSlug" element={<CareersJobPage />} />
        <Route path="/careers/:slug" element={<CareersListPage />} />
        <Route path="/hr-offer/:token" element={<OfferAcceptPage />} />
        <Route path="/hr-onboarding/:token" element={<OnboardingPortalPage />} />

        {/* Print previews — no dashboard chrome (reliable browser print) */}
        <Route
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          <Route path="/print/sales/delivery-notes/:id" element={<DeliveryPrintPreviewPage />} />
          <Route path="/print/qms/final-inspection-reports/:id" element={<FinalInspectionReportPrintPage />} />
          <Route path="/print/qms/inspection-release-notes/:id" element={<InspectionReleaseNotePrintPage />} />
          <Route path="/print/qms/certificates-of-conformity/:id" element={<CertificateOfConformityPrintPage />} />
          <Route path="/print/qms/dimensional-inspections/:id" element={<DimensionalInspectionPrintPage />} />
          <Route path="/print/qms/ncrs/:id" element={<NCRPrintPage />} />
          <Route path="/print/growth/customer-satisfaction" element={<CustomerSatisfactionPrintPage />} />
          <Route path="/print/growth/customer-satisfaction/:id" element={<CustomerSatisfactionPrintPage />} />
          <Route path="/print/qms/capas/:id" element={<CAPAPrintPage />} />
          <Route
            path="/print/qms/receiving-dimensional-inspections/:id"
            element={<ReceivingDimensionalInspectionPrintPage />}
          />
          <Route
            path="/print/scm/procurement/receiving-dimensional-inspections/:id"
            element={<ReceivingDimensionalInspectionPrintPage />}
          />
          <Route
            path="/print/qms/receiving-inspections/:id"
            element={<ReceivingInspectionPrintPage />}
          />
          <Route
            path="/print/scm/procurement/receiving-inspections/:id"
            element={<ReceivingInspectionPrintPage />}
          />
        </Route>

        {/* ERP AI Workspace v3 — full-bleed shell */}
        <Route
          element={
            <ProtectedRoute>
              <AiWorkspaceShell />
            </ProtectedRoute>
          }
        >
          <Route path="/ai/workspace" element={<AiWorkspacePage />} />
          <Route path="/ai/dashboard" element={<AiDashboardPage />} />
          <Route path="/ai/udq" element={<AiUdqBuilderPage />} />
          <Route path="/ai/udq/:id" element={<AiUdqBuilderPage />} />
        </Route>

        {/* Owner Intelligence Platform */}
        <Route
          element={
            <ProtectedRoute>
              <OwnerIntelligenceShell />
            </ProtectedRoute>
          }
        >
          <Route path="/owner-intelligence" element={<OwnerIntelligenceHomePage />} />
          <Route path="/owner-intelligence/group" element={<GroupGmDashboardPage />} />
          <Route path="/owner-intelligence/business-health" element={<BusinessHealthPage />} />
          <Route path="/owner-intelligence/financial-health" element={<FinancialHealthPage />} />
          <Route path="/owner-intelligence/profit" element={<GrowthProfitPage />} />
          <Route path="/owner-intelligence/cash" element={<CashIntelligencePage />} />
          <Route path="/owner-intelligence/customers" element={<CustomerIntelligencePage />} />
          <Route path="/owner-intelligence/sales-pipeline" element={<SalesPipelineIntelligencePage />} />
          <Route path="/owner-intelligence/operations" element={<OperationsIntelligencePage />} />
          <Route path="/owner-intelligence/supply-chain" element={<SupplyChainIntelligencePage />} />
          <Route path="/owner-intelligence/kpis" element={<KpiPerformancePage />} />
          <Route path="/owner-intelligence/strategy" element={<StrategyAlignmentPage />} />
          <Route
            path="/owner-intelligence/enterprise-strategy"
            element={<EnterpriseStrategyPage />}
          />
          <Route
            path="/owner-intelligence/enterprise-strategy/*"
            element={<EnterpriseStrategyPage />}
          />
          <Route
            path="/owner-intelligence/enterprise-readiness"
            element={<EnterpriseReadinessPage />}
          />
          <Route path="/owner-intelligence/actions" element={<ExecutiveActionCenterPage />} />
          <Route path="/owner-intelligence/benchmarks" element={<BenchmarkPage />} />
          <Route path="/owner-intelligence/root-cause" element={<RootCausePage />} />
          <Route path="/owner-intelligence/risk" element={<RiskCenterPage />} />
          <Route
            path="/owner-intelligence/operational-intelligence"
            element={<OperationalIntelligencePage />}
          />
          <Route
            path="/owner-intelligence/operational-actions"
            element={<OperationalActionsPage />}
          />
          <Route path="/owner-intelligence/digital-twin" element={<DigitalTwinPage />} />
          <Route path="/owner-intelligence/module-intelligence" element={<ModuleIntelligencePage />} />
          <Route path="/owner-intelligence/automation" element={<AutomationMonitorPage />} />
          <Route
            path="/owner-intelligence/automation/executions/:id"
            element={<AutomationExecutionPage />}
          />
          <Route path="/owner-intelligence/automation/designer" element={<AutomationDesignerPage />} />
          <Route path="/owner-intelligence/scenarios" element={<ScenarioManagerPage />} />
          <Route path="/owner-intelligence/advisor" element={<ExecutiveAdvisorPage />} />
          <Route path="/owner-intelligence/ai-governance" element={<AiGovernancePage />} />
          <Route
            path="/owner-intelligence/organizational-knowledge"
            element={<OrganizationalKnowledgePage />}
          />
          <Route path="/owner-intelligence/memory" element={<ExecutiveMemoryPage />} />
          <Route path="/owner-intelligence/meetings" element={<MeetingModePage />} />
          <Route path="/owner-intelligence/external-context" element={<ExternalContextPage />} />
          <Route path="/owner-intelligence/board-packs" element={<BoardPackPage />} />
        </Route>

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          
          {/* Home Domain */}
          <Route path="/home" element={<HomePage />} />
          <Route path="/education" element={<EducationHomePage />} />
          <Route path="/education/settings" element={<EducationSettingsPage />} />
          <Route path="/education/students" element={<StudentListPage />} />
          <Route path="/education/students/new" element={<StudentFormPage />} />
          <Route path="/education/students/:id/edit" element={<StudentFormPage />} />
          <Route path="/education/students/:id/academic-history" element={<AcademicHistoryPage />} />
          <Route path="/education/students/:id/attendance" element={<StudentAttendanceHistoryPage />} />
          <Route path="/education/students/:id/twin" element={<StudentDigitalTwinPage />} />
          <Route path="/education/students/:id" element={<StudentDetailPage />} />
          <Route path="/education/academic" element={<AcademicSessionsPage />} />
          <Route path="/education/academic/sessions/new" element={<AcademicSessionFormPage />} />
          <Route path="/education/academic/sessions/:id" element={<AcademicSessionDetailPage />} />
          <Route path="/education/policy" element={<PolicyPacksPage />} />
          <Route path="/education/admissions" element={<AdmissionsListPage />} />
          <Route path="/education/admissions/crm" element={<AdmissionsCrmPage />} />
          <Route path="/education/admissions/enquiries" element={<EnquiryListPage />} />
          <Route path="/education/admissions/enquiries/new" element={<EnquiryFormPage />} />
          <Route path="/education/admissions/enquiries/:id" element={<EnquiryFormPage />} />
          <Route path="/education/admissions/new" element={<AdmissionApplicationPage />} />
          <Route path="/education/admissions/:id/review" element={<AdmissionReviewPage />} />
          <Route path="/education/admissions/:id/decision" element={<AdmissionDecisionPage />} />
          <Route path="/education/admissions/:id/offer" element={<AdmissionOfferPage />} />
          <Route path="/education/admissions/:id" element={<AdmissionApplicationPage />} />
          <Route path="/education/enrollments" element={<EnrollmentRegisterPage />} />
          <Route path="/education/enrollments/new" element={<EnrollmentDetailPage />} />
          <Route path="/education/enrollments/:id/allocate" element={<SectionAllocationPage />} />
          <Route path="/education/enrollments/:id/transfer" element={<SectionTransferPage />} />
          <Route path="/education/enrollments/:id/withdrawal" element={<WithdrawalPage />} />
          <Route path="/education/enrollments/:id" element={<EnrollmentDetailPage />} />
          <Route path="/education/progression" element={<ProgressionListPage />} />
          <Route path="/education/progression/new" element={<ProgressionDetailPage />} />
          <Route path="/education/progression/:id/exceptions" element={<ProgressionExceptionsPage />} />
          <Route path="/education/progression/:id" element={<ProgressionDetailPage />} />
          <Route path="/education/transfer-certificates" element={<TransferCertificateListPage />} />
          <Route path="/education/transfer-certificates/new" element={<TransferCertificateDetailPage />} />
          <Route path="/education/transfer-certificates/:id" element={<TransferCertificateDetailPage />} />
          <Route path="/education/reports" element={<EduReportsHubPage />} />
          <Route path="/education/reports/strength" element={<ReportRunnerPage />} />
          <Route path="/education/reports/attendance" element={<ReportRunnerPage />} />
          <Route path="/education/reports/fee-aging" element={<ReportRunnerPage />} />
          <Route path="/education/fees" element={<FeesHubPage />} />
          <Route path="/education/fees/heads" element={<FeeHeadsPage />} />
          <Route path="/education/fees/structures" element={<FeeStructuresPage />} />
          <Route path="/education/fees/structures/:id" element={<FeeStructureDetailPage />} />
          <Route path="/education/fees/demands" element={<FeeDemandsPage />} />
          <Route path="/education/fees/outstanding" element={<FeeOutstandingPage />} />
          <Route path="/education/fees/collection" element={<FeeCollectionWorkbenchPage />} />
          <Route path="/education/fees/family-statement" element={<FeeFamilyStatementPage />} />
          <Route path="/education/fees/promises" element={<FeePromisesPage />} />
          <Route path="/education/fees/disputes/:id/resolve" element={<FeeDisputeResolvePage />} />
          <Route path="/education/fees/disputes" element={<FeeDisputesPage />} />
          <Route path="/education/fees/concessions" element={<FeeConcessionsPage />} />
          <Route path="/education/fees/students/:studentId" element={<StudentFeeLedgerPage />} />
          <Route path="/education/transport" element={<TransportHubPage />} />
          <Route path="/education/transport/routes" element={<TransportRoutesPage />} />
          <Route path="/education/transport/routes/:id" element={<TransportRouteDetailPage />} />
          <Route path="/education/transport/boarding/:id" element={<TransportBoardingRunPage />} />
          <Route path="/education/transport/boarding" element={<TransportBoardingPage />} />
          <Route path="/education/transport/enrollments" element={<TransportEnrollmentsPage />} />
          <Route path="/education/transport/enrollments/new" element={<TransportEnrollmentFormPage />} />
          <Route path="/education/transport/enrollments/bulk" element={<TransportEnrollmentBulkPage />} />
          <Route path="/education/transport/enrollments/:id" element={<TransportEnrollmentFormPage />} />
          <Route path="/education/library" element={<LibraryHubPage />} />
          <Route path="/education/library/titles" element={<LibraryTitlesPage />} />
          <Route path="/education/library/titles/new" element={<LibraryTitleFormPage />} />
          <Route path="/education/library/reservations/new" element={<LibraryReservationFormPage />} />
          <Route path="/education/library/reservations" element={<LibraryReservationsPage />} />
          <Route path="/education/library/settings" element={<LibrarySettingsPage />} />
          <Route path="/education/library/titles/:id/edit" element={<LibraryTitleFormPage />} />
          <Route path="/education/library/titles/:id/copies" element={<LibraryTitleCopiesPage />} />
          <Route path="/education/library/issue" element={<LibraryIssuePage />} />
          <Route path="/education/library/loans" element={<LibraryLoansPage />} />
          <Route path="/education/assessments" element={<AssessmentHubPage />} />
          <Route path="/education/assessments/schemes/new" element={<AssessmentSchemePage />} />
          <Route path="/education/assessments/schemes/:id" element={<AssessmentSchemePage />} />
          <Route path="/education/assessments/schemes/:id/entry" element={<AssessmentEntryPage />} />
          <Route path="/education/assessments/schemes/:id/publish" element={<AssessmentPublishPage />} />
          <Route path="/education/questions" element={<QuestionBankHubPage />} />
          <Route path="/education/questions/items/new" element={<QuestionItemFormPage />} />
          <Route path="/education/questions/items/:id/edit" element={<QuestionItemFormPage />} />
          <Route path="/education/questions/items" element={<QuestionItemsPage />} />
          <Route path="/education/question-blueprints/new" element={<QuestionBlueprintDetailPage />} />
          <Route path="/education/question-blueprints/:id" element={<QuestionBlueprintDetailPage />} />
          <Route path="/education/question-blueprints" element={<QuestionBlueprintsPage />} />
          <Route path="/education/behaviour" element={<BehaviourListPage />} />
          <Route path="/education/behaviour/new" element={<BehaviourFormPage />} />
          <Route path="/education/behaviour/:id" element={<BehaviourDetailPage />} />
          <Route path="/education/inclusion" element={<InclusionHubPage />} />
          <Route path="/education/inclusion/accommodations" element={<InclusionAccommodationListPage />} />
          <Route path="/education/inclusion/accommodations/new" element={<InclusionAccommodationFormPage />} />
          <Route
            path="/education/inclusion/accommodations/:id"
            element={<InclusionAccommodationDetailPage />}
          />
          <Route path="/education/inclusion/screenings" element={<InclusionScreeningListPage />} />
          <Route path="/education/inclusion/screenings/new" element={<InclusionScreeningFormPage />} />
          <Route path="/education/inclusion/screenings/:id" element={<InclusionScreeningDetailPage />} />
          <Route path="/education/intervention" element={<InterventionListPage />} />
          <Route path="/education/intervention/new" element={<InterventionFormPage />} />
          <Route path="/education/intervention/:id" element={<InterventionDetailPage />} />
          <Route path="/education/standards" element={<StandardsListPage />} />
          <Route path="/education/standards/new" element={<StandardsFormPage />} />
          <Route path="/education/standards/:id" element={<StandardsDetailPage />} />
          <Route path="/education/governance" element={<GovernanceHubPage />} />
          <Route path="/education/governance/policies" element={<GovernancePolicyListPage />} />
          <Route path="/education/governance/policies/new" element={<GovernancePolicyFormPage />} />
          <Route path="/education/governance/policies/:id" element={<GovernancePolicyDetailPage />} />
          <Route path="/education/governance/committees" element={<GovernanceCommitteeListPage />} />
          <Route path="/education/governance/committees/new" element={<GovernanceCommitteeFormPage />} />
          <Route path="/education/governance/committees/:id" element={<GovernanceCommitteeDetailPage />} />
          <Route path="/education/governance/meetings/:id" element={<GovernanceMeetingDetailPage />} />
          <Route path="/education/app" element={<AppPlanListPage />} />
          <Route path="/education/app/new" element={<AppPlanFormPage />} />
          <Route path="/education/app/:id" element={<AppPlanDetailPage />} />
          <Route path="/education/eqams" element={<EqamsListPage />} />
          <Route path="/education/eqams/new" element={<EqamsFormPage />} />
          <Route path="/education/eqams/:id" element={<EqamsDetailPage />} />
          <Route path="/education/portal/parent" element={<ParentPortalPage />} />
          <Route path="/education/portal/parent/children/:studentId" element={<ParentPortalChildPage />} />
          <Route path="/education/portal/student" element={<StudentPortalPage />} />
          <Route path="/education/portal/teacher" element={<TeacherPortalPage />} />
          <Route path="/education/udise" element={<UdiseHubPage />} />
          <Route path="/education/udise/maps" element={<UdiseMapListPage />} />
          <Route path="/education/udise/runs" element={<UdiseRunListPage />} />
          <Route path="/education/udise/runs/new" element={<UdiseRunFormPage />} />
          <Route path="/education/udise/runs/:id" element={<UdiseRunDetailPage />} />
          <Route path="/education/ids" element={<IdsHubPage />} />
          <Route path="/education/ids/workflows" element={<IdsWorkflowListPage />} />
          <Route path="/education/ids/workflows/new" element={<IdsWorkflowFormPage />} />
          <Route path="/education/ids/workflows/:id" element={<IdsWorkflowDetailPage />} />
          <Route path="/education/ids/readiness" element={<IdsReadinessPage />} />
          <Route path="/education/comms" element={<CommsHubPage />} />
          <Route path="/education/comms/templates" element={<CommsTemplateListPage />} />
          <Route path="/education/comms/sends" element={<CommsSendListPage />} />
          <Route path="/education/comms/sends/new" element={<CommsSendFormPage />} />
          <Route path="/education/comms/sends/:id" element={<CommsSendDetailPage />} />
          <Route path="/education/print" element={<PrintHubPage />} />
          <Route path="/education/pulse" element={<PrincipalPulsePage />} />
          <Route path="/education/quality-copilot" element={<QualityCopilotPage />} />
          <Route path="/education/process-insights" element={<ProcessInsightsPage />} />
          <Route path="/education/hpc" element={<HpcHubPage />} />
          <Route path="/education/hpc/templates/new" element={<HpcTemplateFormPage />} />
          <Route path="/education/hpc/templates/:id" element={<HpcTemplateFormPage />} />
          <Route path="/education/hpc/stages/new" element={<HpcStageFormPage />} />
          <Route path="/education/hpc/stages/:id" element={<HpcStageDetailPage />} />
          <Route path="/education/lms" element={<LmsHubPage />} />
          <Route path="/education/lms/lesson-plans" element={<LessonPlanListPage />} />
          <Route path="/education/lms/lesson-plans/new" element={<LessonPlanFormPage />} />
          <Route path="/education/lms/lesson-plans/:id/edit" element={<LessonPlanFormPage />} />
          <Route path="/education/lms/homework" element={<HomeworkListPage />} />
          <Route path="/education/lms/homework/new" element={<HomeworkFormPage />} />
          <Route path="/education/lms/homework/:id/edit" element={<HomeworkFormPage />} />
          <Route path="/education/lms/homework/:id/submit" element={<HomeworkSubmitPage />} />
          <Route path="/education/lms/homework/:id" element={<HomeworkDetailPage />} />
          <Route path="/education/lms/resources" element={<ResourceListPage />} />
          <Route path="/education/lms/resources/new" element={<ResourceFormPage />} />
          <Route path="/education/lms/resources/:id/edit" element={<ResourceFormPage />} />
          <Route path="/education/lms/announcements" element={<ClassAnnouncementListPage />} />
          <Route path="/education/lms/announcements/new" element={<ClassAnnouncementFormPage />} />
          <Route path="/education/lms/announcements/:id/edit" element={<ClassAnnouncementFormPage />} />
          <Route path="/education/activities" element={<ActivitiesHubPage />} />
          <Route path="/education/activities/list" element={<ActivitiesListPage />} />
          <Route path="/education/activities/new" element={<ActivityFormPage />} />
          <Route path="/education/activities/memberships/new" element={<ActivityMembershipFormPage />} />
          <Route path="/education/activities/memberships/bulk" element={<ActivityMembershipBulkPage />} />
          <Route path="/education/activities/memberships" element={<ActivityMembershipsPage />} />
          <Route path="/education/activities/achievements/new" element={<ActivityAchievementFormPage />} />
          <Route path="/education/activities/achievements/:id" element={<ActivityAchievementFormPage />} />
          <Route path="/education/activities/achievements" element={<ActivityAchievementsPage />} />
          <Route path="/education/activities/meetings/:id" element={<ActivityMeetingAttendancePage />} />
          <Route path="/education/activities/meetings" element={<ActivityMeetingsPage />} />
          <Route path="/education/activities/:id/edit" element={<ActivityFormPage />} />
          <Route path="/education/activities/:id/members" element={<ActivityMembersPage />} />
          <Route path="/education/timetable" element={<TimetableHubPage />} />
          <Route path="/education/timetable/smart" element={<SmartTimetableStudioPage />} />
          <Route path="/education/timetable/smart/setup" element={<SmartTimetableSetupPage />} />
          <Route path="/education/timetable/smart/scenarios" element={<SmartTimetableScenariosPage />} />
          <Route path="/education/timetable/smart/scenarios/:id" element={<SmartTimetableScenarioDetailPage />} />
          <Route path="/education/timetable/smart/runs/:id" element={<SmartTimetableRunPage />} />
          <Route path="/education/timetable/periods" element={<TimetablePeriodsPage />} />
          <Route path="/education/timetable/periods/new" element={<TimetablePeriodFormPage />} />
          <Route path="/education/timetable/periods/:id/edit" element={<TimetablePeriodFormPage />} />
          <Route path="/education/timetable/rooms" element={<TimetableRoomsPage />} />
          <Route path="/education/timetable/rooms/new" element={<TimetableRoomFormPage />} />
          <Route path="/education/timetable/rooms/:id/edit" element={<TimetableRoomFormPage />} />
          <Route path="/education/timetable/grids" element={<TimetableGridsPage />} />
          <Route path="/education/timetable/grids/:id/slots/new" element={<TimetableSlotFormPage />} />
          <Route path="/education/timetable/grids/:id/slots/:slotId/edit" element={<TimetableSlotFormPage />} />
          <Route path="/education/timetable/grids/:id" element={<TimetableGridDetailPage />} />
          <Route path="/education/timetable/teacher" element={<TimetableTeacherPage />} />
          <Route path="/education/substitutions" element={<SubstitutionsListPage />} />
          <Route path="/education/substitutions/new" element={<SubstitutionDayFormPage />} />
          <Route
            path="/education/substitutions/:id/covers/:coverId/assign"
            element={<SubstitutionCoverAssignPage />}
          />
          <Route path="/education/substitutions/:id" element={<SubstitutionDayDetailPage />} />
          <Route path="/education/curriculum" element={<CurriculumHubPage />} />
          <Route path="/education/curriculum/import" element={<CurriculumImportPage />} />
          <Route path="/education/curriculum/units" element={<CurriculumUnitsPage />} />
          <Route path="/education/curriculum/units/new" element={<CurriculumUnitFormPage />} />
          <Route path="/education/curriculum/units/:id/edit" element={<CurriculumUnitFormPage />} />
          <Route
            path="/education/curriculum/units/:id/outcomes/new"
            element={<CurriculumOutcomeFormPage />}
          />
          <Route
            path="/education/curriculum/units/:id/outcomes/:outcomeId/edit"
            element={<CurriculumOutcomeFormPage />}
          />
          <Route path="/education/curriculum/units/:id" element={<CurriculumUnitDetailPage />} />
          <Route path="/education/workspace" element={<TeacherWorkspacePage />} />
          <Route path="/education/attendance" element={<AttendanceRegisterPage />} />
          <Route path="/education/attendance/take" element={<TakeAttendancePage />} />
          <Route path="/education/attendance/overview" element={<AttendanceOverviewPage />} />
          <Route path="/education/attendance/exceptions" element={<AttendanceExceptionsPage />} />
          <Route path="/education/attendance/settings" element={<AttendanceSettingsPage />} />
          <Route path="/education/attendance/calendar" element={<SchoolCalendarPage />} />
          <Route path="/education/attendance/leave" element={<StudentLeavePage />} />
          <Route path="/education/attendance/corrections" element={<AttendanceCorrectionsPage />} />
          <Route path="/education/attendance/:id" element={<TakeAttendancePage />} />
          <Route path="/education/health" element={<CompartmentRecordPage compartment="health" />} />
          <Route path="/education/counseling" element={<CompartmentRecordPage compartment="counseling" />} />
          <Route path="/education/safeguarding" element={<CompartmentRecordPage compartment="safeguarding" />} />
          <Route path="/home/company-overview" element={<CompanyOverviewPage />} />
          <Route path="/home/tasks/:taskId" element={<UnifiedTaskDetailPage />} />
          <Route path="/home/tasks" element={<MyTasksPage />} />
          <Route path="/home/team-tasks" element={<TeamTasksPage />} />
          <Route path="/tasks/module/:moduleKey" element={<ModuleTasksRedirect />} />
          <Route path="/home/drafts" element={<MyDraftsPage />} />
          <Route path="/home/alerts" element={<MyAlertsPage />} />
          <Route path="/home/approvals" element={<ApprovalInboxPage />} />
          <Route path="/home/exceptions" element={<ExceptionsDashboard />} />
          <Route path="/unified" element={<UnifiedEnterpriseDashboard />} />
          <Route path="/reports" element={<ReportsCatalogPage />} />
          <Route path="/reports/audit" element={<ReportsAuditPage />} />
          
          {/* Legacy Module Dashboards - Redirects */}
          <Route path="/modules/masters" element={<Navigate to="/platform/foundation/items" replace />} />
          <Route path="/modules/operations" element={<Navigate to="/growth/leads" replace />} />
          <Route path="/modules/finance" element={<Navigate to="/fin/dashboard" replace />} />
          <Route path="/modules/quality" element={<Navigate to="/qms/dashboard" replace />} />
          <Route path="/modules/projects" element={<Navigate to="/prj/projects" replace />} />
          <Route path="/modules/settings" element={<Navigate to="/platform/setup/company" replace />} />
          
          {/* New Domain Landing Routes */}
          <Route path="/modules/home" element={<Navigate to="/home" replace />} />
          <Route path="/modules/sales" element={<Navigate to="/growth/leads" replace />} />
          <Route path="/modules/scm" element={<Navigate to="/scm/dashboard" replace />} />
          <Route path="/modules/mfg" element={<Navigate to="/mfg/control-tower" replace />} />
          <Route path="/modules/fin" element={<Navigate to="/fin/dashboard" replace />} />
          <Route path="/modules/qms" element={<Navigate to="/qms/dashboard" replace />} />
          <Route path="/modules/prj" element={<Navigate to="/prj/projects" replace />} />
          <Route path="/modules/hr" element={<Navigate to="/hr/dashboard" replace />} />
          <Route path="/modules/analytics" element={<Navigate to="/analytics/executive" replace />} />
          <Route path="/modules/platform" element={<Navigate to="/platform/setup/company" replace />} />
          
          {/* Finance Module */}
          <Route path="/fin/dashboard" element={<FinanceDashboard />} />
          <Route path="/fin/gl/chart-of-accounts" element={<ChartOfAccountsPage />} />
          <Route path="/fin/gl/journals" element={<JournalVouchersPage />} />
          <Route path="/fin/proforma-invoices" element={<SalesInvoicesPage mode="proforma" />} />
          <Route path="/fin/receipt-vouchers" element={<ReceiptVouchersPage />} />
          <Route path="/fin/receipt-vouchers/new" element={<ReceiptVoucherFormPage />} />
          <Route path="/fin/receipt-vouchers/:id" element={<ReceiptVoucherDetailPage />} />
          <Route path="/fin/receipt-vouchers/:id/edit" element={<ReceiptVoucherFormPage />} />
          <Route path="/fin/payment-vouchers" element={<PaymentVouchersPage />} />
          <Route path="/fin/payment-vouchers/new" element={<PaymentVoucherFormRoute />} />
          <Route path="/fin/payment-vouchers/:id" element={<PaymentVoucherDetailPage />} />
          <Route path="/fin/payment-vouchers/:id/edit" element={<PaymentVoucherFormRoute />} />
          <Route path="/finance/ap/supplier-advance-refunds" element={<SupplierAdvanceRefundsPage />} />
          <Route path="/finance/ap/supplier-advance-refunds/new" element={<SupplierAdvanceRefundFormPage />} />
          <Route path="/finance/ap/supplier-advance-refunds/:id" element={<SupplierAdvanceRefundDetailPage />} />
          <Route path="/finance/ap/supplier-advance-refunds/:id/edit" element={<SupplierAdvanceRefundFormPage />} />
          <Route path="/fin/direct-purchase-invoices" element={<DirectPurchaseInvoicesPage />} />
          <Route path="/fin/direct-purchase-invoices/new" element={<DirectPurchaseInvoiceFormPage />} />
          <Route path="/fin/direct-purchase-invoices/:id" element={<DirectPurchaseInvoiceDetailPage />} />
          <Route path="/fin/direct-purchase-invoices/:id/edit" element={<DirectPurchaseInvoiceFormPage />} />
          <Route path="/fin/prepaid" element={<PrepaidWorkspacePage />} />
          <Route path="/fin/prepaid/new" element={<PrepaidContractFormPage />} />
          <Route path="/fin/prepaid/contracts" element={<PrepaidContractsPage />} />
          <Route path="/fin/prepaid/contracts/:id" element={<PrepaidContractDetailPage />} />
          <Route path="/fin/prepaid/contracts/:id/edit" element={<PrepaidContractFormPage />} />
          <Route path="/fin/prepaid/contracts/:id/adjust" element={<PrepaidAdjustmentPage />} />
          <Route path="/fin/prepaid/runs" element={<PrepaidRunsPage />} />
          <Route path="/fin/prepaid/runs/new" element={<PrepaidRunNewPage />} />
          <Route path="/fin/prepaid/runs/:id" element={<PrepaidRunDetailPage />} />
          <Route path="/fin/prepaid/reconciliation" element={<PrepaidReconciliationPage />} />
          <Route path="/fin/prepaid/settings" element={<PrepaidSettingsPage />} />
          <Route path="/fin/prepaid/reports" element={<Navigate to="/fin/prepaid/reports/register" replace />} />
          <Route path="/fin/prepaid/reports/register" element={<PrepaidRegisterReportPage />} />
          <Route path="/fin/prepaid/reports/schedule" element={<PrepaidScheduleReportPage />} />
          <Route path="/fin/prepaid/reports/movement" element={<PrepaidMovementReportPage />} />
          <Route path="/fin/prepaid/reports/forecast" element={<PrepaidForecastReportPage />} />
          <Route path="/fin/prepaid/lines/:id" element={<PrepaidDrillRedirectPage kind="line" />} />
          <Route path="/fin/prepaid/postings/:id" element={<PrepaidDrillRedirectPage kind="posting" />} />
          <Route path="/fin/prepaid/adjustments/:id" element={<PrepaidDrillRedirectPage kind="revision" />} />
          <Route path="/fin/intercompany/affiliates" element={<IntercompanyAffiliatesPage />} />
          <Route path="/fin/intercompany/transactions" element={<IntercompanyTransactionsPage />} />
          <Route path="/fin/intercompany/transactions/new" element={<IntercompanyTransactionFormPage />} />
          <Route path="/fin/intercompany/transactions/:id/edit" element={<IntercompanyTransactionFormPage />} />
          <Route path="/fin/intercompany/transactions/:id" element={<IntercompanyTransactionDetailPage />} />
          <Route path="/fin/reports/intercompany-balance" element={<IntercompanyBalanceReportPage />} />
          <Route path="/fin/intercompany/settlements" element={<IntercompanySettlementsPage />} />
          <Route path="/fin/reports/intercompany-statement" element={<IntercompanyStatementPage />} />
          <Route path="/fin/intercompany/groups" element={<IntercompanyGroupsPage />} />
          <Route path="/fin/reports/intercompany-consolidation" element={<IntercompanyConsolidationPage />} />
          <Route path="/fin/debit-notes" element={<SupplierDebitNotesPage />} />
          <Route path="/fin/debit-notes/new" element={<CreateDebitNotePage />} />
          <Route path="/fin/debit-notes/:id" element={<SupplierDebitNoteDetailPage />} />
          <Route path="/fin/credit-notes" element={<CustomerCreditNotesPage />} />
          <Route path="/fin/credit-notes/new" element={<CustomerCreditNoteFormPage />} />
          <Route path="/fin/credit-notes/:id/edit" element={<CustomerCreditNoteFormPage />} />
          <Route path="/fin/credit-notes/:id" element={<CustomerCreditNoteDetailPage />} />
          <Route path="/fin/currencies" element={<CurrenciesPage />} />
          <Route path="/fin/banking/accounts" element={<BankAccountsPage />} />
          <Route path="/fin/departments" element={<DepartmentsPage />} />
          <Route path="/fin/payment-terms" element={<PaymentTermsPage />} />
          <Route path="/fin/departments/new" element={<DepartmentFormPage />} />
          <Route path="/fin/departments/:id/edit" element={<DepartmentFormPage />} />
          <Route path="/fin/reports" element={<FinancialReportsPage />} />
          <Route path="/fin/reports/ledger" element={<LedgerPage />} />
          <Route path="/fin/reports/project-ledger" element={<ProjectLedgerPage />} />
          <Route path="/fin/reports/project-profit-loss" element={<ProjectProfitLossPage />} />
          <Route path="/fin/reports/monthly-profit-loss" element={<MonthlyProfitLossPage />} />
          <Route path="/fin/reports/operational-profit" element={<OperationalProfitPage />} />
          <Route path="/fin/reports/customer-outstanding" element={<CustomerOutstandingPage />} />
          <Route path="/fin/reports/supplier-outstanding" element={<SupplierOutstandingPage />} />
          <Route path="/fin/reports/customer-statement" element={<CustomerStatementPage />} />
          <Route path="/fin/reports/supplier-statement" element={<SupplierStatementPage />} />
          <Route path="/fin/reports/settlement-register" element={<SettlementRegisterPage />} />
          <Route path="/fin/reports/payment-register" element={<PaymentRegisterPage />} />
          <Route path="/fin/reports/receipt-register" element={<ReceiptRegisterPage />} />
          <Route path="/fin/reports/direct-purchase-invoice-register" element={<DirectPurchaseInvoiceRegisterPage />} />
          <Route path="/fin/reports/supplier-advance-register" element={<SupplierAdvancesReportPage />} />
          <Route path="/fin/tax/vat-return/:id" element={<VatReturnPage />} />
          <Route path="/fin/tax/vat-return" element={<VatReturnPage />} />
          <Route path="/platform/foundation/currencies" element={<CurrenciesPage />} />
          <Route path="/platform/foundation/revenue-service-types" element={<RevenueServiceTypeMasterPage />} />
          
          {/* Legacy /sales URLs (module renamed to Growth & Revenue) */}
          <Route path="/sales" element={<LegacyPrefixRedirect />} />
          <Route path="/sales/*" element={<LegacyPrefixRedirect />} />

          {/* Growth & Revenue - Lead to Quote */}
          <Route path="/growth/leads" element={<LeadsPage />} />
          <Route path="/growth/leads/new" element={<LeadFormPage />} />
          <Route path="/growth/leads/:id/edit" element={<LeadFormPage />} />
          <Route path="/growth/leads/:id" element={<LeadDetailPage />} />
          <Route path="/growth/opportunities" element={<OpportunitiesPage />} />
          <Route path="/growth/opportunities/new" element={<OpportunityFormPage />} />
          <Route path="/growth/opportunities/:id/edit" element={<OpportunityFormPage />} />
          <Route path="/growth/opportunities/:id" element={<OpportunityDetailPage />} />
          <Route path="/growth/calendar" element={<SalesCalendarPage />} />
          <Route path="/growth/activities" element={<DailyActivityLogPage />} />
          <Route path="/growth/activities/new" element={<CrmActivityFormPage />} />
          <Route path="/growth/activities/:id/edit" element={<CrmActivityFormPage />} />
          <Route path="/growth/follow-ups" element={<FollowUpTrackerPage />} />
          <Route path="/growth/standup" element={<StandupRepPage />} />
          <Route path="/growth/standup/team" element={<StandupTeamPage />} />
          <Route path="/growth/standup/history" element={<StandupHistoryPage />} />
          <Route path="/growth/setup/activity-types" element={<CrmActivityTypesPage />} />
          <Route path="/growth/demos" element={<DemoRegisterPage />} />
          <Route path="/growth/demos/new" element={<DemoFormPage />} />
          <Route path="/growth/demos/:id" element={<DemoFormPage />} />
          <Route path="/growth/sales-targets" element={<SalesTargetsPage />} />
          <Route path="/growth/reports/weekly-sales-performance" element={<WeeklySalesPerformancePage />} />
          <Route path="/growth/quotations" element={<QuotationsPage />} />
          <Route path="/growth/setup/quotation-defaults" element={<QuotationDefaultsPage />} />
          <Route path="/growth/quotations/new" element={<QuotationFormPage />} />
          <Route path="/growth/quotations/edit/:id" element={<QuotationFormPage />} />
          <Route path="/growth/quotations/:id" element={<QuotationDetailPage />} />
          <Route
            path="/growth/quotations/:id/estimate-reconciliation"
            element={<QuotationEstimateReconciliationWorkspace />}
          />
          <Route path="/growth/quotations/:id/source-sync" element={<QuotationSourceSyncWorkspace />} />
          <Route path="/growth/contract-reviews" element={<ContractReviewRegisterPage />} />
          <Route path="/growth/contract-reviews/:id" element={<ContractReviewDetailPage />} />
          <Route path="/growth/order-review-checklists" element={<OrderReviewChecklistRegisterPage />} />
          <Route path="/growth/order-review-checklists/:id" element={<OrderReviewChecklistDetailPage />} />
          <Route path="/growth/customer-satisfaction" element={<CustomerSatisfactionRegisterPage />} />
          <Route path="/growth/customer-satisfaction/:id" element={<CustomerSatisfactionDetailPage />} />

          {/* Customer Property (CSPM) */}
          <Route path="/cspm" element={<CustomerPropertyOperationsPage />} />
          <Route path="/cspm/requirements/new" element={<CustomerPropertyRequirementPage />} />
          <Route path="/cspm/requirements/:id" element={<CustomerPropertyRequirementPage />} />
          <Route path="/cspm/receipts/new" element={<CustomerPropertyReceiptPage />} />
          <Route path="/cspm/receipts/:id/passport" element={<CustomerPropertyPassportPage />} />
          <Route path="/cspm/receipts/:id" element={<CustomerPropertyReceiptPage />} />
          <Route path="/cspm/returns/new" element={<CustomerPropertyReturnPage />} />
          <Route path="/cspm/returns/:id" element={<CustomerPropertyReturnPage />} />
          <Route path="/cspm/reconciliations" element={<CustomerPropertyReconciliationPage />} />
          <Route path="/cspm/reconciliations/:id" element={<CustomerPropertyReconciliationPage />} />
          
          {/* Sales - Cost Estimation Module */}
          <Route path="/growth/estimations" element={<EstimationDashboardPage />} />
          <Route path="/growth/estimations/create" element={<EstimationBuilderPage />} />
          <Route path="/growth/estimations/edit/:id" element={<EstimationBuilderPage />} />
          <Route path="/growth/estimations/multi-item/new" element={<MultiItemEstimationBuilderPage />} />
          <Route path="/growth/estimations/multi-item/:id" element={<MultiItemEstimationBuilderPage />} />
          <Route path="/growth/estimations/ai-predictor" element={<AICostPredictorPage />} />
          <Route path="/growth/estimations/version-comparison" element={<VersionComparisonPage />} />
          <Route path="/growth/estimations/margin-rules" element={<MarginRulesPage />} />
          <Route path="/growth/estimations/historical-analysis" element={<HistoricalAnalysisPage />} />
          
          {/* Sales - BOQ Multi-Item Estimations */}
          <Route path="/growth/estimations/boq" element={<BOQListPage />} />
          <Route path="/growth/estimations/boq/create" element={<BOQBuilderPage />} />
          <Route path="/growth/estimations/boq/edit/:id" element={<BOQBuilderPage />} />
          <Route path="/growth/estimations/boq/import" element={<BOQImportWizard />} />
          <Route path="/growth/estimations/trading-rental" element={<TradingRentalEstimationsPage />} />
          <Route path="/growth/estimations/trading-rental/create" element={<TradingRentalEstimationBuilderPage />} />
          <Route path="/growth/estimations/trading-rental/:id" element={<TradingRentalEstimationBuilderPage />} />
          <Route path="/growth/estimations/assembly" element={<AssemblyEstimateListPage />} />
          <Route path="/growth/estimations/assembly/new" element={<AssemblyEstimateBuilderPage />} />
          <Route path="/growth/estimations/assembly/:id" element={<AssemblyEstimateBuilderPage />} />
          
          {/* Sales Module - Orders & Customers */}
          <Route path="/growth/customers/:id/360" element={<Customer360Page />} />
          <Route path="/growth/customers/new" element={<CustomerFormPage />} />
          <Route path="/growth/customers/:id/edit" element={<CustomerFormPage />} />
          <Route path="/growth/customers" element={<CustomersPage />} />
          <Route path="/growth/customer-success" element={<CustomerSuccessPage />} />
          <Route path="/growth/order-health" element={<OrderHealthHubPage />} />
          <Route path="/growth/quotation-consolidation" element={<QuotationConsolidationPage />} />
          <Route path="/growth/quotation-conversion-reports" element={<QuotationConversionReportsPage />} />
          <Route path="/growth/reports/tre-overhead" element={<TradingRentalOverheadReportsPage />} />
          <Route path="/growth/reports/project-costing" element={<SalesProjectCostingReportPage />} />
          <Route path="/growth/reports/conversion-funnel" element={<SalesConversionFunnelPage />} />
          <Route path="/growth/reports/win-loss" element={<SalesWinLossPage />} />
          <Route path="/growth/reports/opportunity-conversion" element={<SalesOpportunityConversionPage />} />
          <Route path="/growth/reports/salesperson-performance" element={<SalesRepPerformancePage />} />
          <Route path="/growth/reports/product-rst-mix" element={<SalesProductMixPage />} />
          <Route path="/growth/reports/booking-billing-collections" element={<SalesBookingBillingBridgePage />} />
          <Route path="/growth/reports/quotation-aging" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/opportunity-aging" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/credit-note-register" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/opportunity-register" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/opportunity-to-fulfillment" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/quotation-list-register" element={<ReportRunnerPage />} />
          <Route path="/growth/inland-process-deviations" element={<InlandProcessDeviationsPage />} />
          <Route path="/growth/inland-process-deviations/:id" element={<InlandProcessDeviationDetailPage />} />
          <Route path="/growth/orders" element={<SalesOrdersPage />} />
          <Route path="/growth/orders/new" element={<SalesOrderFormPage />} />
          <Route path="/growth/orders/edit/:id" element={<SalesOrderFormPage />} />
          <Route path="/growth/order-timesheets" element={<SalesOrderTimesheetRegisterPage />} />
          <Route path="/growth/orders/:id/create-work-order" element={<SalesOrderToWorkOrderPage />} />
          <Route path="/growth/orders/:id/timesheets" element={<SalesOrderTimesheetPage />} />
          <Route path="/growth/orders/:id/status-check" element={<SalesOrderStatusCheckPage />} />
          <Route path="/growth/orders/:id" element={<SalesOrderDetailPage />} />
          <Route path="/growth/invoices" element={<SalesInvoicesPage />} />
          <Route path="/growth/invoices/:id" element={<SalesInvoiceDetailPage />} />
          <Route path="/growth/delivery-readiness" element={<DeliveryReadinessDashboardPage />} />
          <Route path="/growth/delivery-notes" element={<DeliveriesPage />} />
          <Route path="/growth/delivery-notes/:id" element={<DeliveryDetailPage />} />
          <Route path="/growth/document-collections" element={<DocumentCollectionListPage />} />
          <Route path="/growth/document-collections/:id" element={<DocumentCollectionDetailPage />} />
          <Route path="/growth/my-deliveries" element={<MyDeliveriesPage />} />
          <Route path="/growth/packing-list" element={<DeliveriesPage />} />
          <Route path="/growth/dashboard" element={<SalesDashboard />} />
          <Route path="/growth/dashboards/team-command-center" element={<SalesTeamCommandCenterPage />} />
          <Route path="/growth/reports" element={<SalesReportsHubPage />} />
          <Route path="/growth/reports/invoice-register" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/order-register" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/delivery-note-register" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/customer-analysis" element={<ReportRunnerPage />} />
          <Route path="/growth/reports/invoice-aging" element={<ReportRunnerPage />} />
          
          {/* Supply Chain - Dashboard */}
          <Route path="/scm/dashboard" element={<SupplyChainDashboard />} />
          <Route path="/scm/reports" element={<ScmReportsHubPage />} />
          <Route path="/scm/reports/purchase-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/grn-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/supplier-invoice-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/purchase-return-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/purchase-requisition-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/po-aging" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/supplier-analysis" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/service-receipt-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/stock-movement" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/material-request-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/material-issue-register" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/inventory-valuation" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/gl-reconciliation" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/project-material-reconciliation" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/stock-aging" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/abc-analysis" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/dead-stock" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/purchase-price-variance" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/demand-planning" element={<DemandPlanningPage />} />
          <Route path="/scm/reports/landed-cost-revalue" element={<LandedCostRevaluePage />} />
          <Route path="/scm/reports/cspm-on-hand" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/cspm-awaiting-inspection" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/cspm-exceptions" element={<ReportRunnerPage />} />
          <Route path="/scm/reports/cspm-quality-hold" element={<ReportRunnerPage />} />
          {/* Supply Chain - Procurement */}
          <Route path="/scm/procurement/suppliers/new" element={<SupplierFormPage />} />
          <Route path="/scm/procurement/suppliers/:id/edit" element={<SupplierFormPage />} />
          <Route path="/scm/procurement/suppliers/:id/360" element={<Supplier360Page />} />
          <Route path="/scm/procurement/suppliers/:id" element={<Supplier360Page />} />
          <Route path="/scm/procurement/suppliers" element={<SuppliersPage />} />
          <Route path="/scm/procurement/control-tower" element={<ProcurementControlTowerPage />} />
          <Route path="/scm/procurement/supplier-categories" element={<SupplierCategoryMasterPage />} />
          <Route path="/scm/procurement/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/scm/procurement/purchase-orders/new" element={<PurchaseOrderCreatePage />} />
          <Route path="/scm/procurement/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
          <Route path="/scm/procurement/purchase-orders/:id/edit" element={<PurchaseOrderEditPage />} />
          <Route path="/scm/procurement/supplier-advance-requests" element={<SupplierAdvanceRequestsPage />} />
          <Route path="/scm/procurement/supplier-advance-requests/:id" element={<SupplierAdvanceRequestDetailPage />} />
          <Route path="/scm/procurement/grn" element={<GoodsReceiptNotesPage />} />
          <Route path="/scm/procurement/grn/new" element={<CreateGRNPage />} />
          <Route path="/scm/procurement/grn/:id" element={<GoodsReceiptNoteDetailPage />} />
          <Route path="/scm/procurement/grn/:id/edit" element={<EditGRNPage />} />
          <Route
            path="/scm/procurement/receiving-inspections"
            element={<ReceivingInspectionsPage />}
          />
          <Route
            path="/scm/procurement/receiving-inspections/:id"
            element={<ReceivingInspectionDetailPage />}
          />
          <Route
            path="/qms/receiving-dimensional-inspections"
            element={<ReceivingDimensionalInspectionsPage />}
          />
          <Route
            path="/qms/receiving-dimensional-inspections/:id"
            element={<ReceivingDimensionalInspectionDetailPage />}
          />
          <Route
            path="/qms/receiving-inspections"
            element={<Navigate to="/scm/procurement/receiving-inspections" replace />}
          />
          <Route
            path="/qms/receiving-inspections/:id"
            element={<RedirectReceivingInspectionDetail />}
          />
          <Route
            path="/scm/procurement/receiving-dimensional-inspections"
            element={<Navigate to="/qms/receiving-dimensional-inspections" replace />}
          />
          <Route
            path="/scm/procurement/receiving-dimensional-inspections/:id"
            element={<RedirectReceivingDimensionalInspectionDetail />}
          />
          <Route path="/scm/procurement/service-receipts" element={<ServiceReceiptsPage />} />
          <Route path="/scm/procurement/service-receipts/new" element={<CreateServiceReceiptPage />} />
          <Route path="/scm/procurement/service-receipts/:id" element={<ServiceReceiptDetailPage />} />
          <Route path="/scm/procurement/reports/po-milestones" element={<PoMilestoneRegisterPage />} />
          <Route path="/scm/procurement/supplier-invoices" element={<SupplierInvoicesPage />} />
          <Route path="/scm/procurement/supplier-invoices/new" element={<CreateSupplierInvoicePage />} />
          <Route path="/scm/procurement/three-way-matching" element={<ThreeWayMatchingPage />} />
          <Route path="/scm/procurement/supplier-invoices/:id" element={<SupplierInvoiceDetailPage />} />
          <Route path="/scm/procurement/supplier-invoices/:id/edit" element={<EditSupplierInvoicePage />} />
          <Route path="/scm/procurement/purchase-returns" element={<PurchaseReturnsPage />} />
          <Route path="/scm/procurement/purchase-returns/new" element={<CreatePurchaseReturnPage />} />
          <Route path="/scm/procurement/purchase-returns/:id" element={<PurchaseReturnDetailPage />} />
          <Route path="/scm/procurement/purchase-returns/:id/edit" element={<PurchaseReturnEditPage />} />
          <Route path="/scm/procurement/purchase-requests" element={<PurchaseRequestsPage />} />
          <Route path="/scm/procurement/purchase-requests/new" element={<PurchaseRequestFormPage />} />
          <Route path="/scm/procurement/purchase-requests/:id" element={<PurchaseRequestDetailPage />} />
          <Route path="/scm/procurement/purchase-requests/:id/edit" element={<PurchaseRequestFormPage />} />
          <Route path="/scm/procurement/spot-purchase" element={<SpotPurchaseSupervisorPage />} />
          <Route path="/scm/procurement/supplier-inquiries" element={<SupplierInquiriesPage />} />
          <Route path="/scm/procurement/supplier-inquiries/new" element={<CreateSupplierInquiryPage />} />
          <Route path="/scm/procurement/supplier-inquiries/:id" element={<SupplierInquiryDetailPage />} />
          <Route path="/scm/procurement/supplier-quotations" element={<SupplierQuotationsPage />} />
          <Route path="/scm/procurement/supplier-quotations/:id" element={<SupplierQuotationDetailPage />} />
          <Route path="/scm/procurement/bid-analyses" element={<BidAnalysesPage />} />
          <Route path="/scm/procurement/bid-analyses/:id" element={<BidAnalysisDetailPage />} />
          
          {/* Supply Chain - Supplier Quality */}
          <Route path="/scm/quality/criticality-risk" element={<SupplierCriticalityRiskPage />} />
          <Route path="/scm/quality/approved-supplier-list" element={<ApprovedSupplierListPage />} />
          <Route path="/scm/quality/supplier-assessments/new" element={<CreateSupplierAssessmentPage />} />
          <Route path="/scm/quality/supplier-assessments/:id" element={<SupplierAssessmentDetailPage />} />
          <Route path="/scm/quality/supplier-assessments" element={<SupplierAssessmentPage />} />
          <Route path="/scm/quality/performance-monitoring" element={<SupplierPerformanceDashboard />} />
          <Route path="/scm/quality/receiving-verification" element={<ReceivingVerificationPage />} />
          <Route path="/scm/quality/critical-mtc" element={<CriticalMtcGapsPage />} />
          <Route path="/scm/quality/outsourcing" element={<OutsourcingManagementPage />} />
          
          {/* Supply Chain - Specifications */}
          <Route path="/scm/procurement/specifications" element={<SpecificationBuilderPage />} />
          <Route path="/scm/procurement/technical-requirements" element={<SpecificationBuilderPage />} />
          
          {/* Supply Chain - Inventory Control */}
          <Route path="/scm/inventory/items/new" element={<ItemFormPage />} />
          <Route path="/scm/inventory/items/:id/edit" element={<ItemFormPage />} />
          <Route path="/scm/inventory/items/:id" element={<ItemDetailPage />} />
          <Route path="/scm/inventory/items" element={<ItemsPage />} />
          <Route path="/asset/assets/new" element={<AssetFormPage />} />
          <Route path="/asset/assets/:id/edit" element={<AssetFormPage />} />
          <Route path="/asset/assets/:id/movements/:type" element={<AssetMovementFormPage />} />
          <Route path="/asset/assets/:id/pmv/activate" element={<AssetPmvActivateFormPage />} />
          <Route path="/asset/assets/:id" element={<AssetViewPage />} />
          <Route path="/asset/assets" element={<AssetsPage />} />
          <Route path="/asset/scan" element={<AssetScanPage />} />
          <Route path="/asset/workshop/jobs/:caseId" element={<WorkshopJobWorkspacePage />} />
          <Route path="/asset/workshop" element={<WorkshopOperationsPage />} />
          <Route path="/asset/maintenance/cases/new" element={<AssetMaintenanceCaseFormPage />} />
          <Route path="/asset/maintenance/cases/:id" element={<AssetMaintenanceCaseFormPage />} />
          <Route path="/asset/maintenance/pm-plans/new" element={<AssetMaintenancePmFormPage />} />
          <Route path="/asset/maintenance/pm-plans/:id/complete" element={<AssetMaintenancePmCompletePage />} />
          <Route path="/asset/maintenance/pm-plans/:id" element={<AssetMaintenancePmFormPage />} />
          <Route path="/asset/maintenance/amc/new" element={<AssetMaintenanceAmcFormPage />} />
          <Route path="/asset/maintenance/amc/:id" element={<AssetMaintenanceAmcFormPage />} />
          <Route path="/asset/maintenance" element={<AssetMaintenancePage />} />
          <Route path="/asset/depreciation" element={<AssetDepreciationPage />} />
          <Route path="/asset/disposal" element={<AssetDisposalPage />} />
          <Route path="/asset/accounting-defaults" element={<AssetAccountingDefaultsPage />} />
          <Route path="/asset/groups" element={<AssetGroupsPage />} />
          <Route path="/asset/insurance/policies/new" element={<AssetInsurancePolicyFormPage />} />
          <Route path="/asset/insurance/policies/:id" element={<AssetInsurancePolicyFormPage />} />
          <Route path="/asset/insurance/claims/new" element={<AssetInsuranceClaimFormPage />} />
          <Route path="/asset/insurance/claims/:id" element={<AssetInsuranceClaimFormPage />} />
          <Route path="/asset/insurance" element={<AssetInsurancePage />} />
          <Route path="/asset/audit/cycles/new" element={<AssetAuditCycleFormPage />} />
          <Route path="/asset/audit/cycles/:id/edit" element={<AssetAuditCycleFormPage />} />
          <Route path="/asset/audit/cycles/:cycleId/results/:resultId" element={<AssetAuditResultFormPage />} />
          <Route path="/asset/audit/cycles/:id" element={<AssetAuditCycleWorkspacePage />} />
          <Route path="/asset/audit" element={<AssetAuditPage />} />
          <Route path="/asset/audit/report" element={<AssetAuditReportPage />} />
          <Route path="/asset/reports" element={<AssetReportsPage />} />
          <Route path="/asset/reports/movements" element={<AssetMovementReportPage />} />
          <Route path="/asset/reports/location" element={<AssetLocationReportPage />} />
          <Route path="/asset/pmv/hub" element={<PmvHubPage />} />
          <Route path="/asset/pmv/control-tower" element={<PmvControlTowerPage />} />
          <Route path="/asset/pmv/connected" element={<PmvConnectedOverviewPage />} />
          <Route path="/asset/pmv/connected/map" element={<PmvFleetMapPage />} />
          <Route path="/asset/pmv/connected/movement-attention" element={<PmvSpatialAttentionPage />} />
          <Route path="/asset/pmv/connected/geofences" element={<PmvGeofenceListPage />} />
          <Route path="/asset/pmv/connected/geofences/new" element={<PmvGeofenceFormPage />} />
          <Route path="/asset/pmv/connected/geofences/:id" element={<PmvGeofenceFormPage />} />
          <Route path="/asset/pmv/connected/devices" element={<PmvConnectedDevicesPage />} />
          <Route path="/asset/pmv/connected/providers" element={<PmvConnectedProvidersPage />} />
          <Route path="/asset/pmv/connected/reconciliation" element={<PmvConnectedReconciliationPage />} />
          <Route path="/asset/pmv/connected/diagnostics" element={<PmvConnectedDiagnosticsPage />} />
          <Route path="/asset/pmv/operations" element={<PmvOperationsBoardPage />} />
          <Route path="/asset/pmv/requests" element={<PmvRequestRegisterPage />} />
          <Route path="/asset/pmv/requests/new" element={<PmvRequestFormPage />} />
          <Route path="/asset/pmv/requests/:id/edit" element={<PmvRequestFormPage />} />
          <Route path="/asset/pmv/requests/:id" element={<PmvRequestWorkspacePage />} />
          <Route path="/asset/pmv/allocations/:id/receive" element={<PmvSiteReceiptFormPage />} />
          <Route path="/asset/pmv/allocations/:id" element={<PmvAllocationWorkspacePage />} />
          <Route path="/asset/pmv/daily-logs" element={<PmvDailyLogRegisterPage />} />
          <Route path="/asset/pmv/daily-logs/:id" element={<PmvDailyLogWorkspacePage />} />
          <Route path="/asset/pmv/daily-log-sheets" element={<PmvDailyLogSheetListPage />} />
          <Route path="/asset/pmv/daily-log-sheets/bulk" element={<PmvDailyLogSheetListPage />} />
          <Route path="/asset/pmv/daily-log-sheets/exceptions" element={<PmvFieldOpsExceptionsPage />} />
          <Route path="/asset/pmv/daily-log-sheets/:id" element={<PmvDailyLogSheetPage />} />
          <Route path="/asset/pmv/hire" element={<PmvHireRegisterPage />} />
          <Route path="/asset/pmv/hire/control" element={<PmvHireControlTowerPage />} />
          <Route path="/asset/pmv/hire/agreements" element={<PmvHireAgreementPage />} />
          <Route path="/asset/pmv/hire/agreements/:id" element={<PmvHireAgreementPage />} />
          <Route path="/asset/pmv/hire/on-off" element={<PmvHireOnOffHirePage />} />
          <Route path="/asset/pmv/hire/timesheets" element={<PmvHireTimesheetPage />} />
          <Route path="/asset/pmv/hire/timesheets/:id" element={<PmvHireTimesheetPage />} />
          <Route path="/asset/pmv/hire/statements" element={<PmvHireStatementPage />} />
          <Route path="/asset/pmv/hire/statements/:id" element={<PmvHireStatementPage />} />
          <Route path="/asset/pmv/hire/reconciliations" element={<PmvHireInvoiceReconPage />} />
          <Route path="/asset/pmv/hire/reconciliations/:id" element={<PmvHireInvoiceReconPage />} />
          <Route path="/asset/pmv/hire/attention" element={<PmvHireAttentionPage />} />
          <Route path="/asset/pmv/setup/classifications/new" element={<PmvClassificationFormPage />} />
          <Route path="/asset/pmv/setup/classifications/:id" element={<PmvClassificationFormPage />} />
          <Route path="/asset/pmv/setup/inspection-templates/new" element={<PmvInspectionTemplateFormPage />} />
          <Route path="/asset/pmv/setup/document-requirements/new" element={<PmvDocumentRequirementFormPage />} />
          <Route path="/asset/pmv/setup/hire-rates/new" element={<PmvInternalHireRateFormPage />} />
          <Route path="/asset/pmv/setup/hire-rates/:id" element={<PmvInternalHireRateFormPage />} />
          <Route path="/asset/pmv/setup" element={<PmvSetupPage />} />
          <Route path="/asset/pmv/components/control-tower" element={<PmvComponentControlTowerPage />} />
          <Route path="/asset/pmv/components/import" element={<PmvComponentImportPage />} />
          <Route path="/asset/pmv/components" element={<PmvComponentRegisterPage />} />
          <Route path="/asset/pmv/components/:id" element={<PmvComponentPassportPage />} />
          <Route path="/asset/pmv/fuel/control-tower" element={<PmvFuelControlTowerPage />} />
          <Route path="/asset/pmv/fuel/reconciliation" element={<PmvFuelReconciliationPage />} />
          <Route path="/asset/pmv/fuel/attention" element={<PmvFuelAttentionPage />} />
          <Route path="/asset/pmv/fuel/sources" element={<PmvFuelSourcesPage />} />
          <Route path="/asset/pmv/fuel" element={<PmvFuelRegisterPage />} />
          <Route path="/asset/pmv/reliability/control-tower" element={<PmvReliabilityControlTowerPage />} />
          <Route path="/asset/pmv/reliability/attention" element={<PmvReliabilityAttentionPage />} />
          <Route path="/asset/pmv/reliability/failures" element={<PmvReliabilityFailuresPage />} />
          <Route path="/asset/pmv/reliability/faults" element={<PmvReliabilityFaultsPage />} />
          <Route path="/asset/pmv/reliability/predictions" element={<PmvReliabilityPredictionsPage />} />
          <Route path="/asset/pmv/reliability/forecast" element={<PmvReliabilityForecastPage />} />
          <Route path="/asset/pmv/reliability/conditions" element={<PmvReliabilityConditionPage />} />
          <Route path="/asset/pmv/reliability" element={<PmvReliabilityRegisterPage />} />
          <Route path="/asset/pmv" element={<PmvRegisterPage />} />
          <Route path="/asset/pmv/:id/inspections/new" element={<PmvRecordInspectionFormPage />} />
          <Route path="/asset/pmv/:id/breakdowns/new" element={<PmvReportBreakdownFormPage />} />
          <Route path="/asset/pmv/:id/fuel/new" element={<PmvRecordFuelFormPage />} />
          <Route path="/asset/pmv/:id/operators/assign" element={<PmvAssignOperatorFormPage />} />
          <Route path="/asset/pmv/:id/status" element={<PmvChangeStatusFormPage />} />
          <Route path="/asset/pmv/:id/specifications/edit" element={<PmvEditSpecificationsFormPage />} />
          <Route path="/asset/pmv/:id/tyres" element={<PmvTyreSchematicPage />} />
          <Route path="/asset/pmv/:id/batteries" element={<PmvBatterySchematicPage />} />
          <Route path="/asset/pmv/:id" element={<Pmv360Page />} />
          <Route path="/prj/projects/:id/pmv" element={<ProjectPmvPage />} />
          <Route path="/scm/inventory/item-categories" element={<ItemCategoryMasterPage />} />
          <Route path="/scm/inventory/uom-units" element={<UomMasterPage />} />
          <Route path="/scm/inventory/engineering" element={<EngineeringMaterialsPage />} />
          <Route path="/scm/inventory/material-requests" element={<MaterialRequestsPage />} />
          <Route path="/scm/inventory/material-requests/new" element={<MaterialRequestCreatePage />} />
          <Route path="/scm/inventory/material-requests/:id/edit" element={<MaterialRequestEditPage />} />
          <Route path="/scm/inventory/material-requests/:id" element={<MaterialRequestDetailPage />} />
          <Route path="/scm/inventory/material-issues" element={<MaterialIssuesPage />} />
          <Route path="/scm/inventory/material-issues/:id" element={<MaterialIssueDetailPage />} />
          <Route path="/scm/inventory/material-returns" element={<MaterialReturnsPage />} />
          <Route path="/scm/inventory/site-materials" element={<ProjectSiteMaterialsPage />} />
          <Route path="/scm/inventory/stock-summary" element={<StockManagementPage />} />
          <Route path="/scm/inventory/heat-stock" element={<HeatStockPage />} />
          <Route path="/inventory/warehouses" element={<WarehousesPage />} />
          <Route path="/inventory/item-classification-gl-accounts" element={<ItemClassificationGlPage />} />
          <Route path="/scm/warehouse/warehouses/:id" element={<WarehouseDetailPage />} />
          <Route path="/scm/warehouse/warehouses" element={<WarehousesPage />} />
          <Route path="/scm/warehouse/stock-transfer/new" element={<StockTransferDetailPage />} />
          <Route path="/scm/warehouse/stock-transfer/:id" element={<StockTransferDetailPage />} />
          <Route path="/scm/warehouse/stock-transfer" element={<StockTransfersPage />} />
          <Route path="/scm/warehouse/stock-adjustment/:id" element={<StockAdjustmentDetailPage />} />
          <Route path="/scm/warehouse/stock-adjustment" element={<StockAdjustmentsPage />} />
          <Route path="/scm/inventory/opening-stock" element={<OpeningStockUploadPage />} />
          <Route path="/scm/inventory/stock-count/new" element={<StockCountDetailPage />} />
          <Route path="/scm/inventory/stock-count/:id" element={<StockCountDetailPage />} />
          <Route path="/scm/inventory/stock-count" element={<StockCountsPage />} />
          <Route path="/scm/inventory/putaway" element={<PutawayPage />} />
          <Route path="/scm/inventory/consignment-receipts/new" element={<ConsignmentReceiptDetailPage />} />
          <Route path="/scm/inventory/consignment-receipts/:id" element={<ConsignmentReceiptDetailPage />} />
          <Route path="/scm/inventory/consignment-receipts" element={<ConsignmentReceiptsPage />} />
          <Route path="/scm/inventory/consignment-returns/new" element={<ConsignmentReturnDetailPage />} />
          <Route path="/scm/inventory/consignment-returns/:id" element={<ConsignmentReturnDetailPage />} />
          <Route path="/scm/inventory/consignment-returns" element={<ConsignmentReturnsPage />} />
          
          {/* Projects Module */}
          <Route path="/prj/projects/new" element={<ProjectFormPage />} />
          <Route path="/prj/projects/:id/edit" element={<ProjectFormPage />} />
          <Route path="/prj/projects/:id/budgets" element={<ProjectBudgetWorkbenchPage />} />
          <Route path="/prj/projects/:id/budgets/variance" element={<ProjectBudgetVsActualPage />} />
          <Route path="/prj/projects/:id/status-check" element={<ProjectStatusCheckPage />} />
          <Route path="/prj/projects/:id/costs" element={<ProjectCostsPage />} />
          <Route path="/prj/budget-revisions/:revisionId" element={<ProjectBudgetRevisionBridgePage />} />
          <Route path="/prj/budget-exceedances/:exceedanceId" element={<ProjectBudgetExceedanceBridgePage />} />
          <Route path="/prj/projects" element={<ProjectsPage />} />
          <Route path="/prj/budgets" element={<ProjectBudgetHubPage />} />
          <Route path="/epc" element={<EpcHubPage />} />
          <Route path="/epc/execution" element={<EpcConstructionExecutionPage />} />
          <Route path="/epc/libraries/wbs" element={<EpcWbsLibraryPage />} />
          <Route path="/epc/libraries/:tab" element={<EpcEstimatingLibrariesPage />} />
          <Route path="/epc/libraries" element={<EpcEstimatingLibrariesPage />} />
          <Route path="/epc/commercial" element={<EpcCommercialHubPage />} />
          <Route
            path="/epc/commercial/pursuits/resolve/:entityType/:entityId"
            element={<PursuitResolveBridgePage />}
          />
          <Route
            path="/epc/commercial/pursuits/:opportunityId"
            element={<CommercialPursuitWorkspacePage />}
          />
          <Route path="/epc/commercial/invitations/new" element={<EpcInvitationCreatePage />} />
          <Route path="/epc/commercial/prequalifications/:id/continue" element={<EpcEoiContinuePage />} />
          <Route path="/epc/commercial/prequalifications/:id" element={<EpcPrequalificationDetailPage />} />
          <Route path="/epc/commercial/prequalifications" element={<EpcPrequalificationsPage />} />
          <Route path="/epc/commercial/tenders" element={<EpcTendersPage />} />
          <Route path="/epc/commercial/tenders/:id/bid" element={<EpcBidControlRoomPage />} />
          <Route path="/epc/commercial/tenders/:id" element={<EpcTenderWorkspacePage />} />
          <Route path="/epc/commercial/bid-decisions" element={<EpcBidDecisionsPage />} />
          <Route path="/epc/commercial/awards" element={<EpcAwardsPage />} />
          <Route path="/epc/commercial/contracts" element={<EpcContractsPage />} />
          <Route path="/epc/commercial/contracts/new" element={<EpcContractCreatePage />} />
          <Route path="/epc/commercial/ca-doc/:entityType/:entityId" element={<EpcCaDocumentBridgePage />} />
          <Route
            path="/epc/commercial/estimate-revision/:revisionId"
            element={<EpcEstimateRevisionBridgePage />}
          />
          <Route path="/epc/commercial/contracts/:id/control" element={<EpcContractAdminControlPage />} />
          <Route
            path="/epc/commercial/contracts/:id/payment-applications/new"
            element={<EpcPaymentApplicationFormPage />}
          />
          <Route
            path="/epc/commercial/contracts/:id/payment-applications/:paId"
            element={<EpcPaymentApplicationFormPage />}
          />
          <Route path="/epc/commercial/contracts/:id" element={<EpcContractAdminWorkspacePage />} />
          <Route path="/epc/ipc" element={<EpcIpcRegisterPage />} />
          <Route path="/epc/ipc/certifications" element={<EpcCertificationsRegisterPage />} />
          <Route path="/epc/commercial/subcontracts" element={<EpcSubcontractsPage />} />
          <Route path="/epc/commercial/subcontracts/new" element={<EpcSubcontractAgreementFormPage />} />
          <Route path="/epc/commercial/subcontracts/:id/edit" element={<EpcSubcontractAgreementFormPage />} />
          <Route path="/epc/commercial/subcontracts/:id/control" element={<EpcSubcontractControlPage />} />
          <Route
            path="/epc/commercial/subcontracts/:id/certificates/new"
            element={<EpcSubcontractPaymentCertificateFormPage />}
          />
          <Route
            path="/epc/commercial/subcontracts/:id/certificates/:spcId"
            element={<EpcSubcontractPaymentCertificateFormPage />}
          />
          <Route path="/epc/commercial/subcontracts/:id" element={<EpcSubcontractWorkspacePage />} />
          <Route path="/epc/commercial/mobilizations" element={<EpcMobilizationsPage />} />
          <Route path="/epc/commercial/control-tower" element={<EpcCommercialControlTowerPage />} />
          <Route path="/prj/project-health" element={<ProjectHealthHubPage />} />
          <Route path="/prj/timesheets" element={<ProjectTimesheetsPage />} />
          <Route path="/prj/costs" element={<ProjectCostsPage />} />
          <Route path="/prj/dashboard" element={<ProjectPortfolioDashboardPage />} />
          <Route path="/prj/reports" element={<PrjReportsHubPage />} />
          <Route path="/prj/reports/financial-summary" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/trial-balance" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/ledger" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/budget-vs-commitment" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/budget-vs-actual" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/budget-vs-commitment-vs-actual" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/cash-flow" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/cost-analysis" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/profitability" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/billing-vs-cost" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/original-vs-current-vs-actual" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/at-risk" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/open-commitments" element={<ReportRunnerPage />} />
          <Route path="/prj/reports/retention-vo-claim-aging" element={<ReportRunnerPage />} />
          <Route path="/epc/reports" element={<EpcReportsHubPage />} />
          <Route path="/epc/reports/ipc-register" element={<ReportRunnerPage />} />
          <Route path="/epc/reports/sca-register" element={<ReportRunnerPage />} />
          <Route path="/hr/reports" element={<HrReportsHubPage />} />
          <Route path="/hr/reports/headcount" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/attendance" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/leave-liability" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/ot" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/payroll-register" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/loans" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/document-expiry" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/eosb" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/allocations" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/project-manpower" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/allocation-exceptions" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/shift-roster" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/timesheet-vs-allocation" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/asset-custody" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/late-register" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/project-labour" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/eosb-settlement" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/eosb-entitlement-statement" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-funnel" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-time-to-hire" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-sources" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-offer-acceptance" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-workload" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-requisition-ageing" element={<ReportRunnerPage />} />
          <Route path="/hr/reports/recruit-onboarding" element={<ReportRunnerPage />} />
          <Route path="/qms/reports" element={<QmsReportsHubPage />} />
          
          {/* Quality & Compliance Module */}
          <Route path="/qms/dashboard" element={<QualityDashboardPage />} />
          <Route path="/qms/quality-plans" element={<QualityPlansPage />} />
          <Route path="/qms/quality-plans/:id" element={<QualityPlanDetailPage />} />
          <Route path="/qms/kpis" element={<DepartmentKPIsPage />} />
          <Route path="/qms/kpi-measurements" element={<KPIMeasurementsPage />} />
          <Route path="/qms/inspections" element={<InspectionsPage />} />
          <Route path="/qms/inspections/:id" element={<InspectionDetailPage />} />
          <Route path="/qms/initial-inspection-requests" element={<InitialInspectionRequestsPage />} />
          <Route path="/qms/initial-inspection-requests/:id" element={<InitialInspectionRequestsPage />} />
          <Route path="/qms/final-inspection-reports/:id" element={<FinalInspectionReportDetailPage />} />
          <Route path="/qms/final-inspection-reports" element={<FinalInspectionReportsPage />} />
          <Route path="/qms/inspection-release-notes/:id" element={<InspectionReleaseNoteDetailPage />} />
          <Route path="/qms/inspection-release-notes" element={<InspectionReleaseNotesPage />} />
          <Route path="/qms/certificates-of-conformity" element={<CertificateOfConformityPage />} />
          <Route path="/qms/equipments" element={<Navigate to="/qms/tmmde" replace />} />
          <Route path="/qms/tmmde/impact/:id" element={<TmmdeImpactDetailPage />} />
          <Route path="/qms/tmmde/impact" element={<TmmdeImpactListPage />} />
          <Route path="/qms/tmmde/:assetId" element={<TmmdeEquipmentDetailPage />} />
          <Route path="/qms/tmmde" element={<TmmdeEquipmentListPage />} />
          <Route path="/qms/dimensional-inspection/new" element={<DimensionalInspectionPage key="dimensional-inspection-new" mode="form" />} />
          <Route path="/qms/dimensional-inspection/:id/edit" element={<DimensionalInspectionPage key="dimensional-inspection-edit" mode="form" />} />
          <Route path="/qms/dimensional-inspection" element={<DimensionalInspectionRegisterPage />} />
          <Route path="/qms/ncrs" element={<NCRsPage />} />
          <Route path="/qms/ncrs/new" element={<NCRFormPage mode="create" />} />
          <Route path="/qms/ncrs/:id/edit" element={<NCRFormPage mode="edit" />} />
          <Route path="/qms/ncrs/:id" element={<NCRDetailPage />} />
          <Route path="/qms/capas" element={<CAPAsPage />} />
          <Route path="/qms/capas/new" element={<CAPAFormPage mode="create" />} />
          <Route path="/qms/capas/:id/edit" element={<CAPAFormPage mode="edit" />} />
          <Route path="/qms/capas/:id" element={<CAPADetailPage />} />
          <Route path="/qms/audits" element={<AuditSchedulesPage />} />
          <Route path="/qms/inspection-library" element={<InspectionPointLibraryPage />} />
          
          {/* Risk & Control Module */}
          <Route path="/grc/risk-assessments" element={<RiskAssessmentsPage />} />
          <Route path="/grc/risk-assessments/:id" element={<RiskAssessmentWorkspacePage />} />
          <Route path="/risk/assessments" element={<RiskAssessmentsPage />} />
          <Route path="/grc/moc/new" element={<MOCNewPage />} />
          <Route path="/grc/moc/reports" element={<MOCReportsPage />} />
          <Route path="/grc/moc/:id" element={<MOCDetailPage />} />
          <Route path="/grc/moc" element={<MOCRegisterPage />} />
          <Route path="/grc/competency" element={<CompetencyPage />} />
          <Route path="/grc/competency/employees/:employeeId" element={<CompetencyEmployeePage />} />
          <Route path="/grc/competency/job-profiles" element={<JobProfilesPage />} />
          <Route path="/grc/competency/job-profiles/:id" element={<JobProfileDetailPage />} />
          <Route path="/grc/competency/matrix" element={<GapMatrixPage />} />
          <Route path="/grc/competency/courses" element={<TrainingCoursesPage />} />
          <Route path="/grc/competency/schedule" element={<TrainingSchedulePage />} />
          <Route path="/grc/competency/sessions" element={<TrainingSessionsPage />} />
          <Route path="/grc/competency/sessions/:id" element={<TrainingSessionDetailPage />} />
          <Route path="/grc/competency/effectiveness" element={<EffectivenessListPage />} />
          <Route path="/grc/competency/effectiveness/:id" element={<EffectivenessDetailPage />} />
          <Route path="/grc/competency/assessments" element={<CompetencyAssessmentsPage />} />
          <Route path="/grc/competency/assessments/:id" element={<CompetencyAssessmentDetailPage />} />
          <Route path="/grc/competency/cycles" element={<AssessmentCyclesPage />} />
          <Route path="/grc/competency/import" element={<CompetencyImportPage />} />
          <Route path="/competency" element={<Navigate to="/grc/competency" replace />} />
          <Route path="/grc/calibration/schedules/new" element={<CalibrationScheduleFormPage />} />
          <Route path="/grc/calibration/schedules/:id/edit" element={<CalibrationScheduleFormPage />} />
          <Route path="/grc/calibration/records/new" element={<CalibrationRecordFormPage />} />
          <Route path="/grc/calibration/records/:id/edit" element={<CalibrationRecordFormPage />} />
          <Route path="/grc/calibration/records/:id" element={<CalibrationRecordDetailPage />} />
          <Route path="/grc/calibration" element={<CalibrationPage />} />
          
          {/* Manufacturing Module */}
          <Route path="/mfg/control-tower" element={<ManufacturingControlTowerPage />} />
          <Route path="/mfg/factory-floor" element={<FactoryFloorDisplayPage />} />
          <Route path="/mfg/workstation-control-center" element={<WorkstationControlCenterPage />} />
          <Route path="/mfg/dashboard" element={<ManufacturingDashboardPage />} />
          <Route path="/mfg/reports" element={<MfgReportsHubPage />} />
          <Route path="/mfg/reports/work-order-register" element={<ReportRunnerPage />} />
          <Route path="/mfg/reports/operations-status" element={<ReportRunnerPage />} />
          <Route path="/mfg/boms/new" element={<BOMFormPage />} />
          <Route path="/mfg/boms/:id" element={<BOMFormPage />} />
          <Route path="/mfg/boms" element={<BOMsPage />} />
          <Route path="/mfg/work-orders/:workOrderId/print" element={<WorkOrderPrintPage />} />
          <Route path="/mfg/work-orders/:workOrderId/route-sheet" element={<WorkOrderRouteSheetPage />} />
          <Route path="/mfg/route-sheet-register" element={<RouteSheetRegisterPage />} />
          <Route path="/mfg/work-orders/:workOrderId" element={<WorkOrdersPage />} />
          <Route path="/mfg/work-orders" element={<WorkOrdersPage />} />
          <Route path="/mfg/production-requests/new" element={<ProductionRequestFormPage />} />
          <Route
            path="/mfg/production-requests/:id/create-work-order"
            element={<ProductionRequestToWorkOrderPage />}
          />
          <Route
            path="/mfg/production-requests/:id/approval-print"
            element={<ProductionRequestApprovalPrintPage />}
          />
          <Route path="/mfg/production-requests/:id" element={<ProductionRequestsPage />} />
          <Route path="/mfg/production-requests" element={<ProductionRequestsPage />} />
          <Route path="/mfg/traceability-matrix" element={<TraceabilityMatrixPage />} />
          <Route path="/mfg/reports/outsourced-fabrication" element={<OutsourcedFabricationReportsPage />} />
          <Route path="/mfg/inland-process-deviations/:id" element={<InlandProcessDeviationDetailPage />} />
          <Route path="/mfg/work-order-operations" element={<WorkOrderOperationsBoardPage />} />
          <Route path="/mfg/work-order-operations/eod-close" element={<EodClosePage />} />
          <Route path="/mfg/stage-master" element={<StageMasterPage />} />
          <Route path="/mfg/shifts" element={<ShiftsPage />} />
          <Route path="/mfg/calendar" element={<ProductionCalendarPage />} />
          <Route path="/mfg/workstations" element={<WorkstationsPage />} />
          <Route path="/mfg/capacity-planning" element={<CapacityPlanningPage />} />
          <Route path="/mfg/material-audit" element={<MaterialAuditPage />} />
          <Route path="/mfg/master-schedule" element={<MasterSchedulePage />} />
          <Route path="/mfg/job-scheduling" element={<JobSchedulingPage />} />
          <Route path="/mfg/operations/new" element={<OperationMasterFormPage />} />
          <Route path="/mfg/operations/:id/edit" element={<OperationMasterFormPage />} />
          <Route path="/mfg/operations" element={<OperationsMasterPage />} />
          <Route path="/mfg/bom-routing" element={<BOMRoutingPage />} />
          <Route path="/mfg/operation-dependencies" element={<OperationDependenciesPage />} />
          <Route path="/mfg/operation-tracking" element={<OperationTimeTrackingPage />} />
          
          {/* HR Module */}
          <Route path="/hr/dashboard" element={<HrmsDashboardPage />} />
          <Route path="/hr/setup" element={<HrmsSetupPage />} />
          <Route path="/hr/accruals" element={<AccrualManagementPage />} />
          <Route path="/hr/holidays" element={<HolidaysPage />} />
          <Route path="/hr/shifts" element={<HrShiftsPage />} />
          <Route path="/hr/locations" element={<LocationsPage />} />
          <Route path="/hr/positions" element={<PositionsPage />} />
          <Route path="/hr/leave-types" element={<LeaveTypesPage />} />
          <Route path="/hr/leave-opening-balances" element={<LeaveOpeningBalancesPage />} />
          <Route path="/hr/grades" element={<GradesPage />} />
          <Route path="/hr/salary-heads" element={<SalaryHeadsPage />} />
          <Route path="/hr/me/leave" element={<EssLeavePage />} />
          <Route path="/hr/me/ot" element={<EssOtPage />} />
          <Route path="/hr/me/payslips" element={<EssPayslipPage />} />
          <Route path="/hr/me/profile" element={<EssProfilePage />} />
          <Route path="/hr/me/letters" element={<EssLettersPage />} />
          <Route path="/hr/me/timesheets/:id" element={<EssTimesheetsPage />} />
          <Route path="/hr/me/timesheets" element={<EssTimesheetsPage />} />
          <Route path="/hr/me" element={<EssHomePage />} />
          <Route path="/hr/letter-requests" element={<LetterRequestsPage />} />
          <Route path="/hr/letter-requests/:id" element={<LetterRequestsPage />} />
          <Route path="/hr/employees/new" element={<EmployeeFormPage />} />
          <Route path="/hr/employees/location-assignment" element={<EmployeeLocationAssignmentPage />} />
          <Route path="/hr/employees/shift-assignment" element={<EmployeeShiftAssignmentPage />} />
          <Route path="/hr/employees/:id/edit" element={<EmployeeFormPage />} />
          <Route path="/hr/employees/:id" element={<EmployeeProfilePage />} />
          <Route path="/hr/profile-change-requests" element={<ProfileChangeRequestsPage />} />
          <Route path="/hr/employees" element={<EmployeesPage />} />
          <Route path="/hr/contracts/new" element={<ContractFormPage />} />
          <Route path="/hr/contracts/:id" element={<ContractDetailPage />} />
          <Route path="/hr/contracts" element={<ContractsPage />} />
          <Route path="/hr/salary-revisions/new" element={<SalaryRevisionFormPage />} />
          <Route path="/hr/salary-revisions/:id/edit" element={<SalaryRevisionFormPage />} />
          <Route path="/hr/salary-revisions/:id" element={<SalaryRevisionDetailPage />} />
          <Route path="/hr/salary-revisions" element={<SalaryRevisionsPage />} />
          <Route path="/hr/allocations/bulk" element={<AllocationsBulkPage />} />
          <Route path="/hr/allocations" element={<AllocationsPage />} />
          <Route path="/hr/asset-custody/new" element={<AssetIssueFormPage />} />
          <Route path="/hr/asset-custody/:id" element={<AssetIssueDetailPage />} />
          <Route path="/hr/asset-custody" element={<AssetCustodyPage />} />
          <Route path="/hr/recruitment" element={<RecruitHomePage />} />
          <Route path="/hr/requisitions/new" element={<RequisitionFormPage />} />
          <Route path="/hr/requisitions/:id/edit" element={<RequisitionFormPage />} />
          <Route path="/hr/requisitions/:id" element={<RequisitionDetailPage />} />
          <Route path="/hr/requisitions" element={<RequisitionsPage />} />
          <Route path="/hr/jobs/new" element={<JobFormPage />} />
          <Route path="/hr/jobs/:id/edit" element={<JobFormPage />} />
          <Route path="/hr/jobs/:id" element={<JobDetailPage />} />
          <Route path="/hr/jobs" element={<JobsPage />} />
          <Route path="/hr/candidates/new" element={<CandidateFormPage />} />
          <Route path="/hr/candidates/import" element={<CandidateImportPage />} />
          <Route path="/hr/candidates/:id/edit" element={<CandidateFormPage />} />
          <Route path="/hr/candidates/:id" element={<CandidateDetailPage />} />
          <Route path="/hr/candidates" element={<CandidatesPage />} />
          <Route path="/hr/applications/new" element={<ApplicationFormPage />} />
          <Route path="/hr/applications/:id" element={<ApplicationDetailPage />} />
          <Route path="/hr/applications" element={<ApplicationsPage />} />
          <Route path="/hr/interviews/new" element={<InterviewFormPage />} />
          <Route path="/hr/interviews/mine" element={<MyInterviewsPage />} />
          <Route path="/hr/interviews/:id/edit" element={<InterviewFormPage />} />
          <Route path="/hr/interviews/:id/scorecard" element={<InterviewScorecardPage />} />
          <Route path="/hr/interviews/:id" element={<InterviewDetailPage />} />
          <Route path="/hr/interviews" element={<InterviewsPage />} />
          <Route path="/hr/assessments/new" element={<AssessmentFormPage />} />
          <Route path="/hr/assessments/:id" element={<AssessmentDetailPage />} />
          <Route path="/hr/assessments" element={<AssessmentsPage />} />
          <Route path="/hr/offers/new" element={<OfferFormPage />} />
          <Route path="/hr/offers/:id/edit" element={<OfferFormPage />} />
          <Route path="/hr/offers/:id/convert" element={<OfferConvertPage />} />
          <Route path="/hr/offers/:id" element={<OfferDetailPage />} />
          <Route path="/hr/offers" element={<OffersPage />} />
          <Route path="/hr/onboarding/new" element={<OnboardingFormPage />} />
          <Route path="/hr/onboarding/my-tasks" element={<MyOnboardingTasksPage />} />
          <Route path="/hr/onboarding/:id" element={<OnboardingDetailPage />} />
          <Route path="/hr/onboarding" element={<OnboardingPage />} />
          <Route path="/hr/careers-settings" element={<CareersSettingsPage />} />
          <Route path="/hr/recruit-reports" element={<RecruitReportsPage />} />
          <Route path="/hr/attendance/generate" element={<TimesheetAutoGeneratePage />} />
          <Route path="/hr/attendance/months/:id" element={<AttendanceMonthDetailPage />} />
          <Route path="/hr/attendance" element={<AttendancePage />} />
          <Route path="/hr/overtime/new" element={<OvertimeFormPage />} />
          <Route path="/hr/overtime/:id" element={<OvertimeDetailPage />} />
          <Route path="/hr/overtime" element={<OvertimePage />} />
          <Route path="/hr/leaves/new" element={<LeaveApplicationFormPage />} />
          <Route path="/hr/leaves/bulk" element={<LeaveBulkAbsencePage />} />
          <Route path="/hr/leaves/:id/edit" element={<LeaveApplicationFormPage />} />
          <Route path="/hr/leaves/:id" element={<LeaveApplicationDetailPage />} />
          <Route path="/hr/leaves" element={<LeavesPage />} />
          <Route path="/hr/leave-settlements/:id" element={<LeaveSettlementDetailPage />} />
          <Route path="/hr/leave-settlements" element={<LeaveSettlementsPage />} />
          <Route path="/hr/adjustments/new" element={<AdjustmentsFormPage />} />
          <Route path="/hr/adjustments" element={<AdjustmentsPage />} />
          <Route path="/hr/loans/new" element={<LoanFormPage />} />
          <Route path="/hr/loans/:id" element={<LoanDetailPage />} />
          <Route path="/hr/loans" element={<LoansPage />} />
          <Route path="/hr/payroll/periods/new" element={<PayrollPeriodFormPage />} />
          <Route path="/hr/payroll/periods/:id" element={<PayrollPeriodDetailPage />} />
          <Route path="/hr/payroll" element={<PayrollPage />} />
          <Route path="/hr/eosb/new" element={<EosbFormPage />} />
          <Route path="/hr/eosb/profile/:employeeId?" element={<EosbEmployeeProfilePage />} />
          <Route path="/hr/eosb/:id" element={<EosbDetailPage />} />
          <Route path="/hr/eosb" element={<EosbPage />} />
          <Route path="/hr/document-expiry" element={<DocumentExpiryPage />} />
          <Route path="/hr/audit-log" element={<AuditLogPage />} />
          <Route path="/hr/documents" element={<CompanyDocumentLibraryPage />} />
          <Route path="/platform/documents" element={<CompanyDocumentLibraryPage />} />
          <Route path="/documents" element={<Navigate to="/platform/documents" replace />} />
          
          {/* Compliance Module */}
          <Route path="/compliance/uae-einvoicing" element={<UAEEInvoicingPage />} />
          <Route path="/compliance/inbound-einvoices" element={<InboundEInvoicesPage />} />
          <Route path="/compliance/ksa-zatca" element={<KSAZATCAPage />} />
          
          {/* Analytics Domain */}
          <Route path="/analytics/executive" element={<ExecutiveDashboard />} />
          <Route path="/analytics/sales" element={<Navigate to="/growth/dashboard?tab=analytics" replace />} />
          <Route path="/analytics/scm" element={<SCMAnalytics />} />
          <Route path="/analytics/mfg" element={<MFGAnalytics />} />
          <Route path="/analytics/quality" element={<QualityAnalytics />} />
          <Route path="/ai/enterprise-memory" element={<EnterpriseMemoryPage />} />
          <Route path="/ai/enterprise-memory/:id" element={<EnterpriseMemoryDetailPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/help/:guideId" element={<HelpGuideDetailPage />} />
          
          {/* Platform Module */}
          <Route path="/platform/companies" element={<PlatformCompaniesPage />} />
          <Route path="/platform/companies/new" element={<CreateTenantWizardPage />} />
          <Route path="/platform/companies/:tenantId" element={<PlatformTenantDetailPage />} />
          <Route path="/platform/setup/company" element={<CompanyProfilePage />} />
          <Route path="/platform/setup/configuration-studio" element={<ConfigurationStudioPage />} />
          <Route path="/platform/setup/system" element={<SystemSettingsPage />} />
          <Route path="/platform/setup/api-keys" element={<ApiKeysPage />} />
          <Route path="/settings/api-keys" element={<Navigate to="/platform/setup/api-keys" replace />} />
          <Route path="/platform/setup/status" element={<SystemStatusPage />} />
          <Route path="/platform/setup/sequences" element={<DocumentSequencesPage />} />
          <Route path="/platform/setup/responsibility-rules" element={<ResponsibilityRulesPage />} />
          <Route path="/platform/setup/email-templates" element={<EmailTemplatesPage />} />
          <Route path="/platform/setup/task-sla" element={<UnifiedTaskSlaDefinitionsPage />} />
          <Route path="/platform/security/users/new" element={<UserCreatePage />} />
          <Route path="/platform/security/users/:userId/edit" element={<UserEditPage />} />
          <Route path="/platform/security/users" element={<UsersPage />} />
          <Route path="/platform/security/roles" element={<RolesPage />} />
          <Route path="/platform/security/permissions" element={<PermissionsPage />} />
          <Route path="/platform/ai-agent/audit" element={<AiAgentAuditPage />} />
          <Route path="/platform/ai-agent/config" element={<AiAgentConfigPage />} />
          <Route path="/platform/recognition/lab" element={<RecognitionLabPage />} />
          <Route path="/platform/recognition/analytics" element={<RecognitionAnalyticsPage />} />
          <Route path="/platform/workflows/approvals/new" element={<ApprovalWorkflowFormPage />} />
          <Route path="/platform/workflows/approvals/:id/edit" element={<ApprovalWorkflowFormPage />} />
          <Route path="/platform/workflows/approvals" element={<ApprovalWorkflowsPage />} />
          <Route path="/platform/workflows/rejection-reasons" element={<RejectionReasonsSettingsPage />} />
          <Route path="/platform/rejection-register" element={<RejectionRegisterPage />} />
          <Route path="/platform/preferences" element={<UserPreferencesPage />} />
          <Route path="/platform/sales-teams" element={<SalesTeamsPage />} />
          <Route path="/platform/sales-team-transfers" element={<SalesTeamTransferRegisterPage />} />
          <Route path="/platform/missing-team-assignment" element={<MissingTeamAssignmentPage />} />
          <Route path="/profile" element={<UserProfilePage />} />
          
          {/* Platform - Foundation Data */}
          <Route path="/platform/foundation/customers/:id/360" element={<Customer360Page />} />
          <Route path="/platform/foundation/customers/new" element={<CustomerFormPage />} />
          <Route path="/platform/foundation/customers/:id/edit" element={<CustomerFormPage />} />
          <Route path="/platform/foundation/customers" element={<CustomersPage />} />
          <Route path="/platform/foundation/suppliers/new" element={<SupplierFormPage />} />
          <Route path="/platform/foundation/suppliers/:id/edit" element={<SupplierFormPage />} />
          <Route path="/platform/foundation/suppliers/:id/360" element={<Supplier360Page />} />
          <Route path="/platform/foundation/suppliers/:id" element={<Supplier360Page />} />
          <Route path="/platform/foundation/suppliers" element={<SuppliersPage />} />
          <Route path="/platform/foundation/supplier-categories" element={<SupplierCategoryMasterPage />} />
          <Route path="/platform/foundation/items/new" element={<ItemFormPage />} />
          <Route path="/platform/foundation/items/:id/edit" element={<ItemFormPage />} />
          <Route path="/platform/foundation/items/:id" element={<ItemDetailPage />} />
          <Route path="/platform/foundation/items" element={<ItemsPage />} />
          <Route path="/platform/foundation/warehouses" element={<WarehousesPage />} />
        </Route>

        {/* 404 route */}
        <Route path="*" element={<div>404 - Page Not Found</div>} />
            </Routes>
          </MenuProvider>
        </NavigationProvider>
      </CurrencyProvider>
        </AntdApp>
      </LocaleProvider>
    </UserPreferencesProvider>
  );
}

export default App;

